import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import client from '../../api/client';
import { useI18n } from '../../lib/i18n';
import { useAuth } from '../../context/AuthContext';
import AgreementPDFPreview from './AgreementPDFPreview';
import { toast } from 'sonner';

// Residential Property Component - State Specific Fields
// NOTE: User details (Licensor/Licensee) are filled in Steps 1-2, not here
// This component only handles PROPERTY-SPECIFIC details
const ResidentialPropertyFields = ({ formData, handleInputChange, STATES, depositLocked }) => {
  const state = formData.property_state;

  // State-specific district/region options (where applicable)
  const stateDistricts = {
    'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer'],
    'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Gandhinagar'],
    'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam'],
    'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Tirupati'],
    'Haryana': ['Gurgaon', 'Noida', 'Faridabad', 'Chandigarh'],
    'Punjab': ['Chandigarh', 'Amritsar', 'Ludhiana', 'Patiala'],
    'Himachal Pradesh': ['Shimla', 'Solan', 'Mandi', 'Kangra'],
    'Uttarakhand': ['Dehradun', 'Nainital', 'Rishikesh', 'Haridwar'],
  };

  // Residential stamp duty rates by state
  const stampDutyRates = {
    'Maharashtra': 0.5,
    'Delhi': 'duration-based',
    'Karnataka': 0.1,
    'Tamil Nadu': 1.0,
    'Uttar Pradesh': 'variable',
    'Rajasthan': 0.25,
    'Gujarat': 0.25,
    'Telangana': 0.25,
    'Andhra Pradesh': 0.3,
    'Haryana': 0.25,
    'Punjab': 0.25,
    'Himachal Pradesh': 0.25,
    'Uttarakhand': 0.25,
    'Goa': 0.25,
    'Assam': 0.25,
    'West Bengal': 0.25,
    'Odisha': 0.25,
    'Jharkhand': 0.25,
    'Chhattisgarh': 0.25,
    'Madhya Pradesh': 0.25,
    'Bihar': 0.25,
    'Kerala': 0.1,
    'Manipur': 0.25,
    'Meghalaya': 0.25,
    'Mizoram': 0.25,
    'Nagaland': 0.25,
    'Sikkim': 0.1,
    'Tripura': 0.25,
  };

  // Calculate residential stamp duty based on state
  const calculateStampDuty = () => {
    const monthlyRent = parseFloat(formData.monthly_rent) || 0;
    const leaseDuration = parseInt(formData.lease_duration_years) || 1;
    const annualRent = monthlyRent * 12;
    const totalRent = annualRent * leaseDuration;

    const rate = stampDutyRates[state];

    if (rate === 'duration-based') {
      // Delhi: Duration-based (but residential is simpler than commercial)
      return Math.round(totalRent * 0.0025);
    } else if (rate === 'variable') {
      // UP: Default to 0.25% for residential
      return Math.round(totalRent * 0.0025);
    } else {
      return Math.round(totalRent * (rate / 100));
    }
  };

  // Note: Stamp duty calculation kept for reference but not displayed in E-Agreement form
  // Only E-Stamp (separate endpoint) will have actual government stamp duty

  return (
    <div style={{ marginTop: '1rem' }}>
      {/* State-Specific Info Banner */}
      <div style={{
        padding: '1rem',
        borderRadius: '8px',
        background: 'rgba(249, 115, 22, 0.1)',
        border: '1px solid rgba(249, 115, 22, 0.2)',
        marginBottom: '1.5rem'
      }}>
        <p style={{ fontSize: '0.85rem', color: '#F97316', fontWeight: 600, margin: 0 }}>
          🏠 Residential Property - {state}
        </p>
      </div>

      {/* PARTY DETAILS REVIEW */}
      <div style={{
        padding: '1rem',
        borderRadius: '8px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        marginBottom: '1.5rem'
      }}>
        <p style={{ fontSize: '0.85rem', color: '#60a5fa', fontWeight: 600, margin: '0 0 0.5rem 0' }}>
          ✓ Party Details (from Steps 1-2)
        </p>
        <p style={{ fontSize: '0.8rem', color: '#a3a3a3', margin: 0, lineHeight: 1.5 }}>
          Property Owner and Tenant details were collected in previous steps. To change party information, go back to Step 1 or Step 2.
        </p>
      </div>

      {/* PROPERTY DETAILS */}
      <fieldset style={{ border: 'none', marginBottom: '2rem' }}>
        <legend style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '1rem', fontSize: '1rem' }}>
          Property Details
        </legend>
        <select name="property_type" value={formData.property_type || 'flat'} onChange={handleInputChange}>
          <option value="flat">Flat</option>
          <option value="house">House</option>
          <option value="villa">Villa</option>
          <option value="studio">Studio</option>
        </select>
        <textarea name="property_address" placeholder="Complete Address" value={formData.property_address || ''} onChange={handleInputChange} />
        <input type="text" name="property_city" placeholder="City" value={formData.property_city || ''} onChange={handleInputChange} />

        {/* State (already selected) */}
        <select name="property_state" value={formData.property_state} onChange={handleInputChange}>
          {STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* State-specific district selector */}
        {stateDistricts[state] && (
          <select name="property_district" value={formData.property_district || ''} onChange={handleInputChange}>
            <option value="">Select {state} District</option>
            {stateDistricts[state].map(dist => (
              <option key={dist} value={dist}>{dist}</option>
            ))}
          </select>
        )}

        <input type="text" name="property_pin" placeholder="PIN Code (6 digits)" value={formData.property_pin || ''} onChange={handleInputChange} />
        <input type="number" name="property_built_area" placeholder="Built-up Area (sq. ft.)" value={formData.property_built_area || ''} onChange={handleInputChange} />
        <input type="number" name="property_carpet_area" placeholder="Carpet Area (sq. ft.)" value={formData.property_carpet_area || ''} onChange={handleInputChange} />
        <input type="number" name="property_rooms" placeholder="Number of Rooms (BHK)" value={formData.property_rooms || ''} onChange={handleInputChange} />
        <input type="text" name="property_floor" placeholder="Floor Number" value={formData.property_floor || ''} onChange={handleInputChange} />
        <input type="text" name="property_building" placeholder="Building Name" value={formData.property_building || ''} onChange={handleInputChange} />
      </fieldset>

      {/* FINANCIAL DETAILS */}
      <fieldset style={{ border: 'none', marginBottom: '2rem' }}>
        <legend style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '1rem', fontSize: '1rem' }}>
          Financial Details
        </legend>
        <input type="number" name="monthly_rent" placeholder="Monthly Rent (₹)" value={formData.monthly_rent || ''} onChange={handleInputChange} />
        <input type="number" name="security_deposit" placeholder="Security Deposit (₹)" value={formData.security_deposit || ''} onChange={handleInputChange} disabled={!!depositLocked} title={depositLocked ? "Deposit carries over from your current tenancy — cannot be changed at renewal" : undefined} />
        {depositLocked && <p style={{ color: '#a3a3a3', fontSize: '0.8rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>Deposit carries over from your current tenancy — cannot be changed at renewal.</p>}
        <input type="number" name="maintenance_charges" placeholder="Maintenance Charges (₹/month, optional)" value={formData.maintenance_charges || ''} onChange={handleInputChange} />

        <select name="electricity_payer" value={formData.electricity_payer || 'tenant'} onChange={handleInputChange}>
          <option value="lessor">Electricity: Landlord pays</option>
          <option value="lessee">Electricity: Tenant pays</option>
          <option value="shared">Electricity: Shared</option>
        </select>

        <select name="water_payer" value={formData.water_payer || 'tenant'} onChange={handleInputChange}>
          <option value="lessor">Water: Landlord pays</option>
          <option value="lessee">Water: Tenant pays</option>
          <option value="shared">Water: Shared</option>
        </select>

        <select name="property_tax_payer" value={formData.property_tax_payer || 'lessor'} onChange={handleInputChange}>
          <option value="lessor">Property Tax: Landlord pays</option>
          <option value="lessee">Property Tax: Tenant pays</option>
        </select>

        <select name="payment_mode" value={formData.payment_mode || 'bank_transfer'} onChange={handleInputChange}>
          <option value="bank_transfer">Payment Mode: Bank Transfer</option>
          <option value="cheque">Payment Mode: Cheque</option>
          <option value="cash">Payment Mode: Cash</option>
        </select>

        <select name="due_date" value={formData.due_date || '1'} onChange={handleInputChange}>
          <option value="">Select Due Date</option>
          {[...Array(31)].map((_, i) => (
            <option key={i + 1} value={i + 1}>{i + 1}</option>
          ))}
        </select>

        <input type="number" name="late_fee" placeholder="Late Payment Fee (₹/day, optional)" value={formData.late_fee || ''} onChange={handleInputChange} />
      </fieldset>

      {/* LEASE TERMS - Now shown in Step 3 "Lease Terms & Duration" section */}
      {/* Removed old duplicate Lease Terms fieldset - all lease duration fields are now in Step 3 */}

      {/* E-AGREEMENT NOTICE - No Stamp Duty */}
      <fieldset style={{ border: 'none', marginBottom: '2rem' }}>
        <legend style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '1rem', fontSize: '1rem' }}>
          E-Agreement Note
        </legend>

        <div style={{
          padding: '1rem',
          borderRadius: '6px',
          background: 'rgba(249, 115, 22, 0.08)',
          border: '1px solid rgba(249, 115, 22, 0.3)',
          marginBottom: '1rem'
        }}>
          <p style={{ fontSize: '0.85rem', color: '#fb923c', fontWeight: 600, margin: '0 0 0.5rem 0' }}>
            ℹ️ E-Agreement (Not Government-Stamped)
          </p>
          <p style={{ fontSize: '0.8rem', color: '#fcd34d', margin: 0, lineHeight: 1.5 }}>
            <strong>Stamp Duty:</strong> Not applicable for E-Agreements. These are digital agreements without government stamp registration.<br/>
            <br/>
            <strong>If you need legal stamp validity:</strong> Use <strong>E-Stamp Agreement</strong> (separate process) which includes government e-stamp registration and official stamp duty.
          </p>
        </div>
      </fieldset>

      {/* ADDITIONAL DETAILS */}
      <fieldset style={{ border: 'none', marginBottom: '2rem' }}>
        <legend style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '1rem', fontSize: '1rem' }}>
          Additional Details
        </legend>
        <input type="text" name="jurisdiction_city" placeholder="Jurisdiction City" value={formData.jurisdiction_city || ''} onChange={handleInputChange} />
        <input type="text" name="broker_name" placeholder="Broker Name (optional)" value={formData.broker_name || ''} onChange={handleInputChange} />
        <input type="text" name="broker_license" placeholder="Broker License (optional)" value={formData.broker_license || ''} onChange={handleInputChange} />
        <input type="text" name="witness1_name" placeholder="Witness 1 Name" value={formData.witness1_name || ''} onChange={handleInputChange} />
        <input type="email" name="witness1_email" placeholder="Witness 1 Email (to send sign link)" value={formData.witness1_email || ''} onChange={handleInputChange} />
        <input type="tel" name="witness1_phone" placeholder="Witness 1 Phone (optional)" value={formData.witness1_phone || ''} onChange={handleInputChange} />
        <input type="text" name="witness2_name" placeholder="Witness 2 Name" value={formData.witness2_name || ''} onChange={handleInputChange} />
        <input type="email" name="witness2_email" placeholder="Witness 2 Email (to send sign link)" value={formData.witness2_email || ''} onChange={handleInputChange} />
        <input type="tel" name="witness2_phone" placeholder="Witness 2 Phone (optional)" value={formData.witness2_phone || ''} onChange={handleInputChange} />
        <p style={{ fontSize: '0.75rem', color: '#a3a3a3', marginTop: '0.5rem' }}>
          If a witness's email is provided, they'll get a link to preview the agreement and sign - no Orello account needed.
        </p>
      </fieldset>
    </div>
  );
};

// Commercial Property Component - State Specific Fields
const CommercialPropertyFields = ({ formData, handleInputChange, STATES, depositLocked }) => {
  const state = formData.property_state;

  // State-specific district/region options
  const stateDistricts = {
    'Delhi': ['Central', 'South', 'North', 'East', 'West'],
    'Karnataka': ['Bangalore', 'Mysore', 'Mangalore', 'Hubli', 'Belgaum'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Salem'],
    'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Agra', 'Noida'],
  };

  // Commercial property type options
  const commercialTypes = {
    'Maharashtra': ['Flat', 'Shop', 'Office', 'Warehouse'],
    'Delhi': ['Office', 'Shop', 'Warehouse', 'Godown'],
    'Karnataka': ['Office', 'Shop', 'Warehouse', 'Industrial'],
    'Tamil Nadu': ['Office', 'Shop', 'Warehouse', 'Industrial'],
    'Uttar Pradesh': ['Office', 'Shop', 'Warehouse', 'Industrial'],
  };

  // Commercial stamp duty rates by state
  const commercialStampDutyRates = {
    'Karnataka': 0.1,
    'Kerala': 0.2,
    'Sikkim': 0.2,
    'Tamil Nadu': 1.0,
    'Delhi': 'duration-based',
    'Uttar Pradesh': 'variable',
    // 0.5% for all others
  };

  // Calculate stamp duty based on state
  const calculateStampDuty = () => {
    const monthlyRent = parseFloat(formData.monthly_rent) || 0;
    const leaseDuration = parseInt(formData.lease_duration_years) || 1;
    const annualRent = monthlyRent * 12;
    const totalRent = annualRent * leaseDuration;

    let stampDuty = 0;
    const rate = commercialStampDutyRates[state];

    if (state === 'Delhi') {
      // Delhi: Duration-based calculation (commercial)
      if (leaseDuration <= 4) {
        stampDuty = (annualRent * 0.02) + 100;
      } else if (leaseDuration <= 10) {
        stampDuty = (annualRent * 0.05) + 100;
      } else {
        stampDuty = (annualRent * 2 * 0.05) + 100;
      }
    } else if (rate === 'variable') {
      // UP: Default to 0.5% for commercial
      stampDuty = Math.round(totalRent * 0.005);
    } else if (rate) {
      // State-specific rate
      stampDuty = Math.round(totalRent * (rate / 100));
    } else {
      // Default: 0.5% (applies to Rajasthan, Gujarat, Telangana, AP, Haryana, Punjab, HP, Uttarakhand, Goa, Assam, WB, Odisha, Jharkhand, Chhattisgarh, MP, Bihar, Manipur, Meghalaya, Mizoram, Nagaland, Tripura, Maharashtra)
      stampDuty = Math.round(totalRent * 0.005);
    }

    return stampDuty;
  };

  // Note: Stamp duty calculation kept for reference but not displayed in E-Agreement form
  // Only E-Stamp (separate endpoint) will have actual government stamp duty

  return (
    <div style={{ marginTop: '1rem' }}>
      {/* State-Specific Info Banner */}
      <div style={{
        padding: '1rem',
        borderRadius: '8px',
        background: 'rgba(249, 115, 22, 0.1)',
        border: '1px solid rgba(249, 115, 22, 0.2)',
        marginBottom: '1.5rem'
      }}>
        <p style={{ fontSize: '0.85rem', color: '#F97316', fontWeight: 600, margin: '0 0 0.5rem 0' }}>
          🏢 Commercial Property - {state} Rules
        </p>
        <p style={{ fontSize: '0.8rem', color: '#a3a3a3', margin: 0, lineHeight: 1.5 }}>
          {state === 'Delhi' && 'Stamp duty in Delhi varies by lease duration (2% for 1-4 years, 5% for 5-10 years, 5% double for 10+ years)'}
          {state === 'Karnataka' && 'Stamp duty: 0.1% of annual rent (nominal ₹50-100 for leases ≤11 months)'}
          {state === 'Tamil Nadu' && 'Stamp duty: 1% of annual rent (higher rate per TN Stamp Act)'}
          {state === 'Maharashtra' && 'Stamp duty: 0.5% of annual rent (standard rate)'}
        </p>
      </div>

      {/* PARTY DETAILS REVIEW */}
      <div style={{
        padding: '1rem',
        borderRadius: '8px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        marginBottom: '1.5rem'
      }}>
        <p style={{ fontSize: '0.85rem', color: '#60a5fa', fontWeight: 600, margin: '0 0 0.5rem 0' }}>
          ✓ Party Details (from Steps 1-2)
        </p>
        <p style={{ fontSize: '0.8rem', color: '#a3a3a3', margin: 0, lineHeight: 1.5 }}>
          Property Owner and Tenant details were collected in previous steps. To change party information, go back to Step 1 or Step 2.
        </p>
      </div>

      {/* PROPERTY DETAILS */}
      <fieldset style={{ border: 'none', marginBottom: '2rem' }}>
        <legend style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '1rem', fontSize: '1rem' }}>
          Property Details
        </legend>
        <select name="commercial_property_type" value={formData.commercial_property_type || ''} onChange={handleInputChange}>
          <option value="">Select Property Type</option>
          {(commercialTypes[state] || []).map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <textarea name="property_address" placeholder="Complete Address" value={formData.property_address || ''} onChange={handleInputChange} />
        <input type="text" name="property_city" placeholder="City" value={formData.property_city || ''} onChange={handleInputChange} />

        {/* State with auto-fill for Delhi/etc */}
        <select name="property_state" value={formData.property_state} onChange={handleInputChange}>
          {STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* State-specific district selector */}
        {stateDistricts[state] && (
          <select name="property_district" value={formData.property_district || ''} onChange={handleInputChange}>
            <option value="">Select {state} District/Area</option>
            {stateDistricts[state].map(dist => (
              <option key={dist} value={dist}>{dist}</option>
            ))}
          </select>
        )}

        <input type="text" name="property_pin" placeholder="PIN Code (6 digits)" value={formData.property_pin || ''} onChange={handleInputChange} />
        <input type="number" name="property_built_area" placeholder="Built-up Area (sq. ft.)" value={formData.property_built_area || ''} onChange={handleInputChange} />
        <input type="number" name="property_carpet_area" placeholder="Carpet Area (sq. ft.)" value={formData.property_carpet_area || ''} onChange={handleInputChange} />
        <input type="text" name="property_floor" placeholder="Floor Number" value={formData.property_floor || ''} onChange={handleInputChange} />
        <input type="text" name="property_building" placeholder="Building Name" value={formData.property_building || ''} onChange={handleInputChange} />
      </fieldset>

      {/* FINANCIAL DETAILS */}
      <fieldset style={{ border: 'none', marginBottom: '2rem' }}>
        <legend style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '1rem', fontSize: '1rem' }}>
          Financial Details
        </legend>
        <input type="number" name="monthly_rent" placeholder="Monthly Rent (₹)" value={formData.monthly_rent || ''} onChange={handleInputChange} />
        <input type="number" name="security_deposit" placeholder="Security Deposit (₹)" value={formData.security_deposit || ''} onChange={handleInputChange} disabled={!!depositLocked} title={depositLocked ? "Deposit carries over from your current tenancy — cannot be changed at renewal" : undefined} />
        {depositLocked && <p style={{ color: '#a3a3a3', fontSize: '0.8rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>Deposit carries over from your current tenancy — cannot be changed at renewal.</p>}
        <input type="number" name="maintenance_charges" placeholder="Maintenance Charges (₹/month, optional)" value={formData.maintenance_charges || ''} onChange={handleInputChange} />
        <input type="number" name="parking_charges" placeholder="Parking Charges (₹/month, optional)" value={formData.parking_charges || ''} onChange={handleInputChange} />

        <select name="electricity_payer" value={formData.electricity_payer || 'lessee'} onChange={handleInputChange}>
          <option value="lessor">Electricity: Landlord pays</option>
          <option value="lessee">Electricity: Tenant pays</option>
          <option value="shared">Electricity: Shared</option>
          {state === 'Delhi' && <option value="metered">Electricity: Metered (with tax %)</option>}
        </select>

        <select name="water_payer" value={formData.water_payer || 'lessee'} onChange={handleInputChange}>
          <option value="lessor">Water: Landlord pays</option>
          <option value="lessee">Water: Tenant pays</option>
          <option value="shared">Water: Shared</option>
          {state === 'Delhi' && <option value="metered">Water: Metered (with tax %)</option>}
        </select>

        {['Maharashtra', 'Karnataka', 'Tamil Nadu', 'Uttar Pradesh'].includes(state) && (
          <select name="gas_payer" value={formData.gas_payer || 'na'} onChange={handleInputChange}>
            <option value="lessor">Gas: Landlord pays</option>
            <option value="lessee">Gas: Tenant pays</option>
            <option value="shared">Gas: Shared</option>
            <option value="na">Gas: Not Applicable</option>
          </select>
        )}

        <select name="property_tax_payer" value={formData.property_tax_payer || 'lessor'} onChange={handleInputChange}>
          <option value="lessor">Property Tax: Landlord pays</option>
          <option value="lessee">Property Tax: Tenant pays</option>
        </select>

        <select name="payment_mode" value={formData.payment_mode || 'bank_transfer'} onChange={handleInputChange}>
          <option value="bank_transfer">Payment Mode: Bank Transfer</option>
          <option value="cheque">Payment Mode: Cheque</option>
          <option value="cash">Payment Mode: Cash</option>
        </select>

        <select name="due_date" value={formData.due_date || '1'} onChange={handleInputChange}>
          <option value="">Select Due Date</option>
          {[...Array(31)].map((_, i) => (
            <option key={i + 1} value={i + 1}>{i + 1}</option>
          ))}
        </select>

        <input type="number" name="late_fee" placeholder="Late Payment Fee (₹/day, optional)" value={formData.late_fee || ''} onChange={handleInputChange} />
      </fieldset>

      {/* LEASE TERMS - Now shown in Step 3 "Lease Terms & Duration" section */}
      {/* Removed old duplicate Lease Terms fieldset - all lease duration fields are now in Step 3 */}

      {/* E-AGREEMENT NOTICE - No Stamp Duty */}
      <fieldset style={{ border: 'none', marginBottom: '2rem' }}>
        <legend style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '1rem', fontSize: '1rem' }}>
          E-Agreement Note
        </legend>

        <div style={{
          padding: '1rem',
          borderRadius: '6px',
          background: 'rgba(249, 115, 22, 0.08)',
          border: '1px solid rgba(249, 115, 22, 0.3)',
          marginBottom: '1rem'
        }}>
          <p style={{ fontSize: '0.85rem', color: '#fb923c', fontWeight: 600, margin: '0 0 0.5rem 0' }}>
            ℹ️ E-Agreement (Not Government-Stamped)
          </p>
          <p style={{ fontSize: '0.8rem', color: '#fcd34d', margin: 0, lineHeight: 1.5 }}>
            <strong>Stamp Duty:</strong> Not applicable for E-Agreements. These are digital agreements without government stamp registration.<br/>
            <br/>
            <strong>If you need legal stamp validity:</strong> Use <strong>E-Stamp Agreement</strong> (separate process) which includes government e-stamp registration and official stamp duty.
          </p>
        </div>
      </fieldset>

      {/* ADDITIONAL DETAILS */}
      <fieldset style={{ border: 'none', marginBottom: '2rem' }}>
        <legend style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '1rem', fontSize: '1rem' }}>
          Additional Details
        </legend>

        <select name="business_type" value={formData.business_type || ''} onChange={handleInputChange}>
          <option value="">Select Business Type</option>
          <option value="office">Office</option>
          <option value="shop">Shop/Retail</option>
          <option value="warehouse">Warehouse</option>
          <option value="factory">Factory/Industrial</option>
          <option value="coworking">Co-Working Space</option>
        </select>

        <input type="text" name="jurisdiction_city" placeholder="Jurisdiction City" value={formData.jurisdiction_city || ''} onChange={handleInputChange} />
        <input type="text" name="broker_name" placeholder="Broker Name (optional)" value={formData.broker_name || ''} onChange={handleInputChange} />
        <input type="text" name="broker_license" placeholder="Broker License (optional)" value={formData.broker_license || ''} onChange={handleInputChange} />
        <input type="text" name="witness1_name" placeholder="Witness 1 Name" value={formData.witness1_name || ''} onChange={handleInputChange} />
        <input type="email" name="witness1_email" placeholder="Witness 1 Email (to send sign link)" value={formData.witness1_email || ''} onChange={handleInputChange} />
        <input type="tel" name="witness1_phone" placeholder="Witness 1 Phone (optional)" value={formData.witness1_phone || ''} onChange={handleInputChange} />
        <input type="text" name="witness2_name" placeholder="Witness 2 Name" value={formData.witness2_name || ''} onChange={handleInputChange} />
        <input type="email" name="witness2_email" placeholder="Witness 2 Email (to send sign link)" value={formData.witness2_email || ''} onChange={handleInputChange} />
        <input type="tel" name="witness2_phone" placeholder="Witness 2 Phone (optional)" value={formData.witness2_phone || ''} onChange={handleInputChange} />
        <p style={{ fontSize: '0.75rem', color: '#a3a3a3', marginTop: '0.5rem' }}>
          If a witness's email is provided, they'll get a link to preview the agreement and sign - no Orello account needed.
        </p>

        {state === 'Delhi' && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
              <input type="checkbox" name="noc_required" checked={formData.noc_required || false} onChange={(e) => handleInputChange({ target: { name: 'noc_required', value: e.target.checked } })} />
              <span>NOC Required</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
              <input type="checkbox" name="rwa_consent_required" checked={formData.rwa_consent_required || false} onChange={(e) => handleInputChange({ target: { name: 'rwa_consent_required', value: e.target.checked } })} />
              <span>RWA Consent Required</span>
            </label>
          </>
        )}

        {['Tamil Nadu', 'Uttar Pradesh'].includes(state) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
            <input type="checkbox" name="gst_applicable" checked={formData.gst_applicable || false} onChange={(e) => handleInputChange({ target: { name: 'gst_applicable', value: e.target.checked } })} />
            <span>GST Applicable (if rent &gt; ₹5,000/month)</span>
          </label>
        )}
      </fieldset>
    </div>
  );
};

const AgreementFormWithPreview = ({ agreementId, brokerId, onAgreementCreated, onStepComplete }) => {
  const { t, lang } = useI18n(); // Get translation function and current language
  const { user } = useAuth(); // Get current user
  const location = useLocation();

  // Check if user is a creator (broker, agent, or self-managed landlord)
  const isCreator = user && (
    user.role === 'broker_admin' ||
    user.role === 'agent' ||
    (user.role === 'landlord' && user.self_managed)
  );

  // Only brokers and agents earn commission — self-managed landlords do not.
  const isBrokerOrAgent = user && (user.role === 'broker_admin' || user.role === 'agent');

  const [currentStep, setCurrentStep] = useState(-1); // Start at step -1 (state selection)
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false); // Disclaimer checkbox
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [currentDraftId, setCurrentDraftId] = useState(null); // Track current draft being edited
  const [expandedDisclaimer, setExpandedDisclaimer] = useState(false); // Expandable disclaimer section
  const [licensorStatus, setLicensorStatus] = useState(null);
  const [licenseeStatus, setLicenseeStatus] = useState(null);

  // Ref to AgreementPDFPreview to capture the exact PDF from preview
  const pdfPreviewRef = useRef(null);

  // Generate persistent UUID for new agreements (ONE UUID, used throughout)
  const [persistentAgreementId, setPersistentAgreementId] = useState(() => {
    // For new agreements: generate UUID once and keep it
    // For existing agreements: use the passed agreementId
    if (agreementId) return agreementId;
    // Generate v4 UUID: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  });

  // Step -1: State Selection (Template Identifier)
  const [selectedState, setSelectedState] = useState('Karnataka'); // Default state - matches mock data

  // Step 0: Agreement Type & Options
  const [agreementType, setAgreementType] = useState(null); // "e_agreement", "e_stamp", "traditional_paper"
  const [depositProtection, setDepositProtection] = useState(null); // true/false
  // Add-on offered when deposit protection is declined: Orello dispute resolution
  // & proof service for a flat ₹1299 (broker/agent earns a flat ₹100 commission).
  const [proofService, setProofService] = useState(false); // true/false
  const [feePayer, setFeePayer] = useState(null); // "tenant", "landlord", "split"

  // Step 8: Tab Navigation
  const [step8Tab, setStep8Tab] = useState('pdf'); // 'pdf' or 'share'

  const [agreementData, setAgreementData] = useState({
    agreement_id: agreementId,
    broker_id: brokerId,
  });

  const [formData, setFormData] = useState({
    // Step 1: Licensor (Residential) / Lessor (Commercial)
    licensor_full_name: '',
    licensor_age: '',
    licensor_fathers_name: '',
    licensor_address: '',
    licensor_pan_aadhaar: '',
    licensor_phone: '',
    licensor_email: '',

    // Commercial: Lessor Details
    lessor_full_name: '',
    lessor_age: '',
    lessor_fathers_name: '',
    lessor_address: '',
    lessor_pan_aadhaar: '',
    lessor_phone: '',
    lessor_email: '',

    // Step 2: Licensee (Residential) / Lessee (Commercial)
    licensee_full_name: '',
    licensee_age: '',
    licensee_fathers_name: '',
    licensee_address: '',
    licensee_pan_aadhaar: '',
    licensee_phone: '',
    licensee_email: '',

    // Commercial: Lessee Details
    lessee_full_name: '',
    lessee_age: '',
    lessee_fathers_name: '',
    lessee_address: '',
    lessee_pan_aadhaar: '',
    lessee_phone: '',
    lessee_email: '',

    // Step 3: Property (property_state will be set from selectedState)
    property_type: 'flat',
    property_address: '',
    property_city: '',
    property_state: 'Karnataka', // Updated from selectedState on state selection
    property_pin: '',
    property_built_area: '',
    property_carpet_area: '',
    property_rooms: '',
    property_floor: '',
    property_building: '',
    property_society: '',

    // Commercial Property Fields
    commercial_property_type: '',
    property_district: '',

    // Step 4: Financial
    monthly_rent: '',
    security_deposit: '',
    maintenance_charges: '',
    electricity_payer: 'tenant',
    water_payer: 'tenant',
    gas_payer: 'tenant',
    internet_payer: 'tenant',
    payment_mode: 'bank_transfer',
    due_date: '1',
    late_fee: '',
    escalation_percent: '',

    // Commercial Financial Fields
    parking_charges: '',
    property_tax_payer: 'lessor',

    // Step 5: Terms
    start_date: '',
    lock_in_months: '0',
    renewal_months: '11',
    vacancy_penalty: '',

    // Commercial Lease Terms
    lease_duration_years: '0',
    lease_duration_months: '0',
    lease_start_date: '',
    end_date: '',

    // Step 6: Amenities
    parking_spaces: '1',
    pets_allowed: 'no',
    fixtures: '',

    // Step 7: Additional
    jurisdiction_city: '',
    broker_name: '',
    broker_license: '',
    witness1_name: '',
    witness1_email: '',
    witness1_phone: '',
    witness2_name: '',
    witness2_email: '',
    witness2_phone: '',

    // Commercial Additional Fields
    business_type: '',
    stamp_duty_payer: 'lessor',
    estump_uin: '',
    noc_required: false,
    rwa_consent_required: false,
    gst_applicable: false,
  });

  const STATES = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
    "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana",
    "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
    "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
    "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
    "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
    "Uttar Pradesh", "Uttarakhand", "West Bengal"
  ];

  // Note: We no longer create agreement upfront
  // The form collects data locally and only calls backend on final submission

  // Update agreement data as form changes (for live preview)
  useEffect(() => {
    const data = {};

    if (currentStep >= 1) {
      data.licensor = {
        full_name: formData.licensor_full_name,
        age: parseInt(formData.licensor_age) || 0,
        fathers_name: formData.licensor_fathers_name,
        address: formData.licensor_address,
        pan_aadhaar: formData.licensor_pan_aadhaar,
        phone: formData.licensor_phone,
        email: formData.licensor_email,
      };
    }

    if (currentStep >= 2) {
      data.licensee = {
        full_name: formData.licensee_full_name,
        age: parseInt(formData.licensee_age) || 0,
        fathers_name: formData.licensee_fathers_name,
        address: formData.licensee_address,
        pan_aadhaar: formData.licensee_pan_aadhaar,
        phone: formData.licensee_phone,
        email: formData.licensee_email,
      };
    }

    if (currentStep >= 3) {
      data.property = {
        property_type: formData.property_type,
        complete_address: formData.property_address,
        city: formData.property_city,
        state: formData.property_state,
        pin_code: formData.property_pin,
        built_up_area: parseFloat(formData.property_built_area) || 0,
        carpet_area: parseFloat(formData.property_carpet_area) || 0,
        no_of_rooms: parseInt(formData.property_rooms) || 0,
        floor_number: formData.property_floor,
        building_name: formData.property_building,
        society_name: formData.property_society,
      };
    }

    if (currentStep >= 4) {
      data.financial = {
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        security_deposit: parseFloat(formData.security_deposit) || 0,
        maintenance_charges: parseFloat(formData.maintenance_charges) || 0,
        electricity_payer: formData.electricity_payer,
        water_payer: formData.water_payer,
        gas_payer: formData.gas_payer,
        internet_payer: formData.internet_payer,
        payment_mode: formData.payment_mode,
        due_date: parseInt(formData.due_date),
        late_fee_amount: parseFloat(formData.late_fee) || 0,
        escalation_percentage: parseFloat(formData.escalation_percent) || 0,
      };
    }

    if (currentStep >= 5) {
      data.terms = {
        start_date: formData.start_date,
        lock_in_months: parseInt(formData.lock_in_months),
        renewal_duration: parseInt(formData.renewal_months),
        vacancy_penalty: parseFloat(formData.vacancy_penalty) || 0,
      };
    }

    if (currentStep >= 6) {
      data.amenities = {
        parking_spaces: parseInt(formData.parking_spaces),
        pets_allowed: formData.pets_allowed,
        fixtures_provided: formData.fixtures.split(',').map(f => f.trim()).filter(f => f),
      };
    }

    if (currentStep >= 7) {
      data.additional = {
        jurisdiction_city: formData.jurisdiction_city,
        broker_name: formData.broker_name,
        broker_license: formData.broker_license,
        witness1_name: formData.witness1_name,
        witness1_email: formData.witness1_email,
        witness1_phone: formData.witness1_phone,
        witness2_name: formData.witness2_name,
        witness2_email: formData.witness2_email,
        witness2_phone: formData.witness2_phone,
      };
    }

    setAgreementData(prev => ({
      ...prev,
      ...data
    }));
  }, [formData, currentStep]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Phone number validation - only allow 10 digits
    if (name.includes('phone')) {
      const cleaned = value.replace(/\D/g, ''); // Remove non-digits
      if (cleaned.length <= 10) {
        setFormData(prev => ({
          ...prev,
          [name]: cleaned
        }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors([]);
  };

  // Validate phone number format
  const validatePhoneNumber = (phone) => {
    if (!phone) return { valid: false, message: 'Phone number is required' };
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length !== 10) {
      return { valid: false, message: `Phone number must be exactly 10 digits. Current: ${cleaned.length} digits` };
    }
    if (!['6', '7', '8', '9'].includes(cleaned[0])) {
      return { valid: false, message: 'Phone number must start with 6, 7, 8, or 9' };
    }
    return { valid: true, message: '' };
  };

  const validatePAN = (pan) => {
    if (!pan) return { valid: false, message: 'PAN is required' };

    // PAN format: AAAA P NNNN C (10 chars)
    // First 5 letters (A-Z)
    // Next 4 digits (0-9)
    // Last letter (A-Z)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    const panUppercase = pan.toUpperCase().replace(/\s/g, '');

    if (panUppercase.length !== 10) {
      return { valid: false, message: `PAN must be exactly 10 characters. Current: ${panUppercase.length}` };
    }

    if (!panRegex.test(panUppercase)) {
      return {
        valid: false,
        message: `PAN format invalid. Expected: AAAAA NNNN C (5 letters + 4 digits + 1 letter). Example: ABCDE1234F`
      };
    }

    return { valid: true, message: '' };
  };

  // Check if licensor and licensee are registered when reaching step 8
  useEffect(() => {
    if (currentStep === 8) {
      checkRegistration();
    }
  }, [currentStep]);

  // Load saved draft if resuming from dashboard
  useEffect(() => {
    const resumeDraftId = location.state?.resumeDraftId;
    if (resumeDraftId) {
      loadDraft(resumeDraftId);
    }
  }, []);

  // Renewal flow: builder was launched from RentalDetail's "Renew Tenancy"
  // button. Deposit becomes read-only and submission targets the renewal
  // endpoint instead of create-with-agreement — everything else about the
  // builder (steps, PDF generation, signing) is identical to a fresh agreement.
  const [renewalOfRentalId] = useState(location.state?.renewalOfRentalId || null);

  // Step 9 (brokerage) — only brokers/agents charge it, so self-managed
  // landlords finish at step 8. Once the agreement is created at step 8 the
  // builder is holding a REAL rental, so step 9 must be strictly terminal:
  // any path back to step 8 would let Complete fire twice and create a
  // duplicate rental + agreement.
  const [createdRentalId, setCreatedRentalId] = useState(null);
  const [brokerageFromTenant, setBrokerageFromTenant] = useState('');
  const [brokerageFromLandlord, setBrokerageFromLandlord] = useState('');
  const showBrokerageStep = isCreator && isBrokerOrAgent;
  const lastStep = showBrokerageStep ? 9 : 8;


  const loadDraft = async (draftId) => {
    setLoading(true);
    try {
      const { data: result } = await client.get(`/agreements/drafts/${draftId}`);
      const draft = result.draft;

        // Load all the draft data back into the form
        setFormData(draft.form_data || {});
        setAgreementType(draft.agreement_type);
        setCurrentStep(draft.current_step || 0);
        setDepositProtection(draft.deposit_protection);
        setProofService(draft.proof_service || false);
        setFeePayer(draft.fee_payer);
        setSelectedState(draft.form_data?.property_state || 'Karnataka');

      // Store draft ID and name so saves update the existing draft
      setCurrentDraftId(draft.draft_id);
      setDraftName(draft.agreement_name);

      toast.success(`✅ Loaded draft: "${draft.agreement_name}". Continue editing!`);
    } catch (error) {
      console.error('Error loading draft:', error);
      toast.error('Error loading draft: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkRegistration = async () => {
    try {
      const errors = [];

      // Check licensor (landlord/property owner) - by phone OR email
      if (formData.licensor_phone || formData.licensor_email) {
        try {
          const licensorRes = await client.post('/agreements/check-registration', {
            phone: formData.licensor_phone || '',
            email: formData.licensor_email || '',
            role: 'landlord'
          });
          setLicensorStatus(licensorRes.data);
        } catch (error) {
          console.error('Error checking licensor registration:', error);
          setLicensorStatus({ registered: false, user_id: null });
        }
      } else {
        setLicensorStatus({ registered: false, user_id: null });
      }

      // Check licensee (tenant) - by phone OR email
      if (formData.licensee_phone || formData.licensee_email) {
        try {
          const licenseeRes = await client.post('/agreements/check-registration', {
            phone: formData.licensee_phone || '',
            email: formData.licensee_email || '',
            role: 'tenant'
          });
          setLicenseeStatus(licenseeRes.data);
        } catch (error) {
          console.error('Error checking licensee registration:', error);
          setLicenseeStatus({ registered: false, user_id: null });
        }
      } else {
        setLicenseeStatus({ registered: false, user_id: null });
      }

      setErrors([]);
    } catch (error) {
      console.error('Error in checkRegistration:', error);
      // Still set default statuses even if check fails
      setLicensorStatus({ registered: false, user_id: null });
      setLicenseeStatus({ registered: false, user_id: null });
    }
  };

  const inviteLicensor = async () => {
    if (!formData.licensor_phone || !formData.licensor_email) {
      setErrors(['Phone and email required for landlord']);
      return;
    }

    setLoading(true);
    try {
      await client.post('/agreements/send-invite', {
        phone: formData.licensor_phone,
        email: formData.licensor_email,
        name: formData.licensor_full_name,
        role: 'landlord',
        inviter_name: 'Your Agent/Broker',
        inviter_role: 'agent'
      });

      setLicensorStatus({ ...licensorStatus, invited: true });
      setErrors([]);
      alert('✅ Invitation sent to landlord!');
    } catch (error) {
      setErrors(['Failed to send invitation']);
    } finally {
      setLoading(false);
    }
  };

  const inviteLicensee = async () => {
    if (!formData.licensee_phone || !formData.licensee_email) {
      setErrors(['Phone and email required for tenant']);
      return;
    }

    setLoading(true);
    try {
      await client.post('/agreements/send-invite', {
        phone: formData.licensee_phone,
        email: formData.licensee_email,
        name: formData.licensee_full_name,
        role: 'tenant',
        inviter_name: 'Your Agent/Broker',
        inviter_role: 'agent'
      });

      setLicenseeStatus({ ...licenseeStatus, invited: true });
      setErrors([]);
      alert('✅ Invitation sent to tenant!');
    } catch (error) {
      setErrors(['Failed to send invitation']);
    } finally {
      setLoading(false);
    }
  };

  const submitStep = async () => {
    // Handle state selection step (-1)
    if (currentStep === -1) {
      if (!selectedState) {
        setErrors(['Please select a state']);
        return;
      }
      // Update property state and move to step 0
      setFormData(prev => ({
        ...prev,
        property_state: selectedState
      }));
      setCurrentStep(0);
      setErrors([]);
      return;
    }

    // Handle step 0 specially
    if (currentStep === 0) {
      handleStep0Next();
      return;
    }

    // Handle step 0.5 specially
    if (currentStep === 0.5) {
      if (!feePayer) {
        setErrors(['Please select a fee payer option']);
        return;
      }
      // All agreement types go to step 1 (need basic details)
      setCurrentStep(1);
      setErrors([]);
      return;
    }

    // Validate all required fields for current step
    const stepErrors = validateAllFieldsInStep();

    if (stepErrors.length > 0) {
      setErrors(stepErrors);
      return;
    }

    setLoading(true);
    try {
      if (currentStep === 9) {
        // Step 9: brokerage on an ALREADY-created rental. Optional — a broker
        // may charge nothing, in which case we just leave without calling.
        const rentalId = createdRentalId || sessionStorage.getItem('orello_created_rental');
        const t = Math.round(Number(brokerageFromTenant || 0) * 100);
        const l = Math.round(Number(brokerageFromLandlord || 0) * 100);
        try {
          if (rentalId && (t > 0 || l > 0)) {
            await client.post(`/rentals/${rentalId}/brokerage`, {
              brokerage_from_tenant: t,
              brokerage_from_landlord: l,
            });
            toast.success('✅ Brokerage saved — both parties have been notified.');
          }
        } catch (brokerageError) {
          // Non-blocking: the agreement already exists, and the same form is
          // available on the rental page. Never strand the user here.
          toast.error(
            (brokerageError.response?.data?.detail || 'Could not save brokerage') +
            ' — you can set it from the rental page.'
          );
        }
        sessionStorage.removeItem('orello_created_rental');
        window.location.replace(`/app/rental/${rentalId}`);
        return;
      }

      if (currentStep < 8) {
        // Steps 1-7: Just move to next step (no backend call needed)
        // PRODUCTION RULE: Skip Step 7 (Charges & Commission) for creators (brokers, agents, self-managed landlords)
        let nextStep = currentStep + 1;
        if (currentStep === 6 && isCreator) {
          // Skip Step 7 for creators, go directly to Step 8
          nextStep = 8;
        }

        setCurrentStep(nextStep);
        if (onStepComplete) {
          onStepComplete(nextStep);
        }
      } else {
        // ========== STEP 8: Create Agreement + Save PDF ==========
        setLoading(true);

        try {
          // 1. GENERATE PDF BLOB
          console.log('📄 [1/4] Capturing PDF from preview...');
          let pdfBlob = null;

          let signatureBoxes = [];
          if (pdfPreviewRef.current) {
            pdfBlob = await pdfPreviewRef.current.generatePDFBlob();
            console.log(`✅ [1/4] PDF blob generated: ${pdfBlob.size} bytes`);
            // Grab the signature-box coordinates measured during generation so the
            // backend can stamp each party's signature into the right box.
            signatureBoxes = pdfPreviewRef.current.getSignatureBoxes?.() || [];
          } else {
            console.error('❌ [1/4] PDF preview ref not available - CRITICAL ERROR');
            throw new Error('PDF generation failed - preview ref not available');
          }

          if (!pdfBlob || pdfBlob.size === 0) {
            console.error('❌ [1/4] PDF blob is empty or invalid');
            throw new Error('PDF generation produced empty blob');
          }

          // 2. CREATE AGREEMENT IN BACKEND
          console.log('📝 [2/4] Creating agreement in backend with ID:', persistentAgreementId);

          const fullFormData = {
            agreement_id: persistentAgreementId,  // Frontend-generated UUID
            signature_boxes: signatureBoxes,      // For stamping signatures onto the PDF
            fee_payer: feePayer,                  // who pays the agreement fee: tenant | landlord | split
            deposit_protection: depositProtection, // whether Orello holds & protects the deposit
            proof_service: depositProtection === false ? proofService : false, // dispute-resolution add-on (only when protection declined)
            broker_id: brokerId,
            licensor: {
              full_name: formData.licensor_full_name,
              age: parseInt(formData.licensor_age) || 0,
              fathers_name: formData.licensor_fathers_name,
              address: formData.licensor_address,
              pan_aadhaar: formData.licensor_pan_aadhaar,
              phone: formData.licensor_phone,
              email: formData.licensor_email,
            },
            licensee: {
              full_name: formData.licensee_full_name,
              age: parseInt(formData.licensee_age) || 0,
              fathers_name: formData.licensee_fathers_name,
              address: formData.licensee_address,
              pan_aadhaar: formData.licensee_pan_aadhaar,
              phone: formData.licensee_phone,
              email: formData.licensee_email,
            },
            property: {
              property_type: formData.property_type,
              complete_address: formData.property_address,
              city: formData.property_city,
              state: formData.property_state,
              pin_code: formData.property_pin,
              built_up_area: parseFloat(formData.property_built_area) || 0,
              carpet_area: parseFloat(formData.property_carpet_area) || 0,
              no_of_rooms: parseInt(formData.property_rooms) || 0,
              floor_number: formData.property_floor,
              building_name: formData.property_building,
              society_name: formData.property_society,
            },
            financial: {
              monthly_rent: parseFloat(formData.monthly_rent) || 0,
              security_deposit: parseFloat(formData.security_deposit) || 0,
              maintenance_charges: parseFloat(formData.maintenance_charges) || 0,
              electricity_payer: formData.electricity_payer,
              water_payer: formData.water_payer,
              gas_payer: formData.gas_payer,
              internet_payer: formData.internet_payer,
              payment_mode: formData.payment_mode,
              due_date: parseInt(formData.due_date),
              late_fee_amount: parseFloat(formData.late_fee) || 0,
              escalation_percentage: parseFloat(formData.escalation_percent) || 0,
            },
            terms: {
              start_date: formData.start_date,
              end_date: formData.end_date,
              lock_in_months: parseInt(formData.lock_in_months),
              renewal_duration: parseInt(formData.renewal_months),
              vacancy_penalty: parseFloat(formData.vacancy_penalty) || 0,
            },
            amenities: {
              parking_spaces: parseInt(formData.parking_spaces),
              pets_allowed: formData.pets_allowed,
              fixtures_provided: formData.fixtures.split(',').map(f => f.trim()).filter(f => f),
            },
            additional: {
              jurisdiction_city: formData.jurisdiction_city,
              broker_name: formData.broker_name,
              broker_license: formData.broker_license,
              witness1_name: formData.witness1_name,
              witness1_email: formData.witness1_email,
              witness1_phone: formData.witness1_phone,
              witness2_name: formData.witness2_name,
              witness2_email: formData.witness2_email,
              witness2_phone: formData.witness2_phone,
            },
          };

          const submitEndpoint = renewalOfRentalId
            ? `/rentals/${renewalOfRentalId}/renew-with-agreement`
            : '/rentals/create-with-agreement';
          const createResponse = await client.post(submitEndpoint, fullFormData);

          if (!createResponse.data.success) {
            throw new Error(`Agreement creation failed: ${createResponse.data.message}`);
          }

          const rentalId = createResponse.data.rental_id;
          const agreementId = createResponse.data.agreement_id;

          console.log(`✅ [2/4] Agreement created with ID: ${agreementId}`);

          // 3 & 4. SAVE PDF TO BACKEND (NON-BLOCKING)
          // The agreement is already created. If the PDF save fails for any reason,
          // we log it but DO NOT block completion - the user still gets their agreement.
          try {
            console.log('🔄 [3/4] Converting PDF to base64...');
            const base64Pdf = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result.split(',')[1]);
              reader.onerror = reject;
              reader.readAsDataURL(pdfBlob);
            });

            console.log(`💾 [4/4] Saving PDF to backend (${base64Pdf.length} bytes)...`);
            await client.post(`/agreements/${agreementId}/save-pdf`,
              { pdf_blob: base64Pdf },
              { timeout: 120000 }
            );
            console.log(`✅ [4/4] PDF saved for agreement: ${agreementId}`);
          } catch (pdfSaveError) {
            // Non-blocking: agreement is created, PDF can be regenerated/re-saved later
            console.warn('⚠️ PDF save failed (non-blocking - agreement still created):',
              pdfSaveError.response?.data?.detail || pdfSaveError.message);
          }

          console.log('✅ AGREEMENT COMPLETE');

          // No payment happens at creation. The agreement fee is now collected at
          // SIGNING time from whichever party owes it (per fee_payer) - see
          // SigningFlowWizard's pay-then-sign flow. The security deposit + deposit
          // protection fee stay tied to the Deposit stage, unaffected by this change.
          toast.success('✅ Agreement created successfully!');
          if (showBrokerageStep) {
            // Hand off to step 9 (brokerage). Stash the ids so a refresh mid-step
            // can still recover — and even if it can't, the same brokerage form
            // lives permanently on the rental page.
            sessionStorage.setItem('orello_created_rental', rentalId);
            setCreatedRentalId(rentalId);
            setCurrentStep(9);
          } else {
            // Use replace() so the completed builder is removed from history - the
            // user cannot press Back into the already-submitted form.
            window.location.replace(`/app/rental/${rentalId}`);
          }
        } catch (step8Error) {
          console.error('❌ AGREEMENT CREATION FAILED:', step8Error);
          const errorMsg = step8Error.response?.data?.detail ||
                          step8Error.response?.data?.message ||
                          step8Error.message ||
                          'Failed to create agreement';
          setErrors([`❌ ${errorMsg}`]);
          toast.error(`Agreement creation failed: ${errorMsg}`);
        }
      }
    } catch (error) {
      console.error('❌ Unexpected error in handleSaveStep:', error);
      setErrors([error.response?.data?.detail || error.message || 'Error saving step']);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Calculate end date from start date and duration (in years)
  const calculateEndDate = (startDateStr, years) => {
    if (!startDateStr) return '';
    try {
      const startDate = new Date(startDateStr);
      const endDate = new Date(startDate.getFullYear() + years, startDate.getMonth(), startDate.getDate());
      // Format as YYYY-MM-DD
      return endDate.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  const extractStepData = (step) => {
    const stepDataMap = {
      1: {
        full_name: formData.licensor_full_name,
        age: parseInt(formData.licensor_age),
        fathers_name: formData.licensor_fathers_name,
        address: formData.licensor_address,
        pan_aadhaar: formData.licensor_pan_aadhaar,
        phone: formData.licensor_phone,
        email: formData.licensor_email,
      },
      2: {
        full_name: formData.licensee_full_name,
        age: parseInt(formData.licensee_age),
        fathers_name: formData.licensee_fathers_name,
        address: formData.licensee_address,
        pan_aadhaar: formData.licensee_pan_aadhaar,
        phone: formData.licensee_phone,
        email: formData.licensee_email,
      },
      3: {
        property_type: formData.property_type,
        complete_address: formData.property_address,
        city: formData.property_city,
        state: formData.property_state,
        pin_code: formData.property_pin,
        built_up_area: parseFloat(formData.property_built_area),
        carpet_area: parseFloat(formData.property_carpet_area),
        no_of_rooms: parseInt(formData.property_rooms),
        floor_number: formData.property_floor,
        building_name: formData.property_building,
        society_name: formData.property_society,
      },
      4: {
        monthly_rent: parseFloat(formData.monthly_rent),
        security_deposit: parseFloat(formData.security_deposit),
        maintenance_charges: parseFloat(formData.maintenance_charges) || 0,
        electricity_payer: formData.electricity_payer,
        water_payer: formData.water_payer,
        gas_payer: formData.gas_payer,
        internet_payer: formData.internet_payer,
        payment_mode: formData.payment_mode,
        due_date: parseInt(formData.due_date),
        late_fee_amount: parseFloat(formData.late_fee) || 0,
        escalation_percentage: parseFloat(formData.escalation_percent) || 0,
      },
      5: {
        start_date: formData.start_date,
        lock_in_months: parseInt(formData.lock_in_months),
        renewal_duration: parseInt(formData.renewal_months),
        vacancy_penalty: parseFloat(formData.vacancy_penalty) || 0,
      },
      6: {
        parking_spaces: parseInt(formData.parking_spaces),
        pets_allowed: formData.pets_allowed,
        fixtures_provided: formData.fixtures.split(',').map(f => f.trim()).filter(f => f),
      },
      7: {
        jurisdiction_city: formData.jurisdiction_city,
        broker_name: formData.broker_name,
        broker_license: formData.broker_license,
        witness1_name: formData.witness1_name,
        witness1_email: formData.witness1_email,
        witness1_phone: formData.witness1_phone,
        witness2_name: formData.witness2_name,
        witness2_email: formData.witness2_email,
        witness2_phone: formData.witness2_phone,
      },
      8: {}
    };

    return stepDataMap[step] || {};
  };

  const handleStep0Next = () => {
    if (!agreementType) {
      setErrors(['Please select an agreement type']);
      return;
    }
    if (depositProtection === null) {
      setErrors(['Please select deposit protection option']);
      return;
    }
    // Fee-payer step (0.5): ask who pays whatever Orello fee this agreement
    // carries. E-stamp/e-agreement always carry one. Traditional paper carries
    // no agreement fee — but it DOES owe ₹1,299 if the dispute-resolution
    // add-on is taken without deposit protection, and that needs a fee payer
    // too (otherwise the backend silently defaults it to the tenant).
    const feeBearing =
      agreementType === 'e_agreement' ||
      agreementType === 'e_stamp' ||
      (agreementType === 'traditional_paper' && depositProtection === false && proofService === true);
    if (feeBearing && feePayer === null) {
      setCurrentStep(0.5);
    } else {
      setCurrentStep(1);
    }
    setErrors([]);
  };

  // Validate step data before proceeding
  // Comprehensive field validation for all steps
  const validateAllFieldsInStep = () => {
    const stepErrors = [];

    if (currentStep === 1) {
      const isCommercial = formData.property_type === 'commercial';
      const prefix = isCommercial ? 'lessor' : 'licensor';

      // Full name
      if (!formData[`${prefix}_full_name`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} full name is required`);

      // Age
      if (!formData[`${prefix}_age`]) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} age is required`);

      // Father's name
      if (!formData[`${prefix}_fathers_name`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} father's name is required`);

      // Address
      if (!formData[`${prefix}_address`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} address is required`);

      // PAN/Aadhaar
      if (!formData[`${prefix}_pan_aadhaar`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} PAN/Aadhaar is required`);
      else {
        const panValidation = validatePAN(formData[`${prefix}_pan_aadhaar`]);
        if (!panValidation.valid) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} PAN: ${panValidation.message}`);
      }

      // Phone
      if (!formData[`${prefix}_phone`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} phone is required`);
      else {
        const phoneValidation = validatePhoneNumber(formData[`${prefix}_phone`]);
        if (!phoneValidation.valid) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} phone: ${phoneValidation.message}`);
      }

      // Email
      if (!formData[`${prefix}_email`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessor' : 'Licensor'} email is required`);
    }

    if (currentStep === 2) {
      const isCommercial = formData.property_type === 'commercial';
      const prefix = isCommercial ? 'lessee' : 'licensee';

      // Full name
      if (!formData[`${prefix}_full_name`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} full name is required`);

      // Age
      if (!formData[`${prefix}_age`]) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} age is required`);

      // Father's name
      if (!formData[`${prefix}_fathers_name`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} father's name is required`);

      // Address
      if (!formData[`${prefix}_address`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} address is required`);

      // PAN/Aadhaar
      if (!formData[`${prefix}_pan_aadhaar`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} PAN/Aadhaar is required`);
      else {
        const panValidation = validatePAN(formData[`${prefix}_pan_aadhaar`]);
        if (!panValidation.valid) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} PAN: ${panValidation.message}`);
      }

      // Phone
      if (!formData[`${prefix}_phone`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} phone is required`);
      else {
        const phoneValidation = validatePhoneNumber(formData[`${prefix}_phone`]);
        if (!phoneValidation.valid) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} phone: ${phoneValidation.message}`);
      }

      // Email
      if (!formData[`${prefix}_email`]?.trim()) stepErrors.push(`${isCommercial ? 'Lessee' : 'Licensee'} email is required`);
    }

    if (currentStep === 3) {
      // Property type
      if (!formData.property_type) stepErrors.push('Property type is required');

      // Address
      if (!formData.property_address?.trim()) stepErrors.push('Property address is required');

      // City
      if (!formData.property_city?.trim()) stepErrors.push('Property city is required');

      // State
      if (!formData.property_state?.trim()) stepErrors.push('Property state is required');

      // PIN
      if (!formData.property_pin?.trim()) stepErrors.push('Property PIN is required');

      // Area
      if (!formData.property_built_area) stepErrors.push('Property built area is required');

      if (formData.property_type === 'commercial') {
        if (!formData.property_carpet_area) stepErrors.push('Property carpet area is required');
      } else {
        if (!formData.property_rooms) stepErrors.push('Number of rooms is required');
      }

      // Lease Duration Fields - Now require start and end dates (not manual duration dropdowns)
      if (!formData.start_date) stepErrors.push('Lease start date is required');
      if (!formData.end_date) stepErrors.push('Lease end date is required');

      // Validate that end date is after start date
      if (formData.start_date && formData.end_date) {
        const startDate = new Date(formData.start_date);
        const endDate = new Date(formData.end_date);
        if (endDate <= startDate) {
          stepErrors.push('Lease end date must be after start date');
        }
      }
    }

    if (currentStep === 4) {
      // Monthly rent
      if (!formData.monthly_rent) stepErrors.push('Monthly rent is required');

      // Security deposit
      if (!formData.security_deposit) stepErrors.push('Security deposit is required');

      // Payment mode
      if (!formData.payment_mode) stepErrors.push('Payment mode is required');
    }

    if (currentStep === 5) {
      // Step 5: Amenities & Policies - No validation needed
      // All fields in this step are optional (parking_spaces, pets_allowed)
    }

    if (currentStep === 6) {
      // Step 6: Additional Details (Amenities & Policies + Additional Details merged)
      // Parking spaces (optional for now, but if user fills it, validate)
      if (formData.parking_spaces && isNaN(formData.parking_spaces)) stepErrors.push('Parking spaces must be a number');
      // Pets allowed - this is a select so it always has a default value

      // Jurisdiction city (required)
      if (!formData.jurisdiction_city?.trim()) stepErrors.push('Jurisdiction city is required');
    }

    // Step 7: Validation skipped for creators
    // Step 7 is "Charges & Commission" - display-only, no required fields
    // Creators skip this step entirely (handled in submitStep)

    return stepErrors;
  };

  // Show Save As dialog
  const handleSaveClick = () => {
    // Generate default name from property and party details
    const defaultName = `${formData.property_address || 'Agreement'} - ${formData.licensor_full_name || formData.lessor_full_name || 'Landlord'} & ${formData.licensee_full_name || formData.lessee_full_name || 'Tenant'}`.slice(0, 100);
    setDraftName(defaultName);
    setShowSaveAsDialog(true);
  };

  // Save agreement as draft with custom name
  const saveAgreementDraft = async () => {
    if (!draftName.trim()) {
      toast.error('Please enter a name for this agreement');
      return;
    }

    setLoading(true);
    try {
      const draftData = {
        draft_id: currentDraftId, // Pass existing draft ID to update instead of create
        broker_id: brokerId,
        agreement_type: agreementType,
        property_type: formData.property_type,
        current_step: currentStep,
        form_data: formData,
        deposit_protection: depositProtection,
        proof_service: proofService,
        fee_payer: feePayer,
        agreement_name: draftName.trim(),
        status: 'draft',
        created_at: new Date().toISOString(),
      };

      const { data: result } = await client.post('/agreements/save-draft', draftData);

      // If this was a new draft, store the ID for future saves
      if (!currentDraftId) {
        setCurrentDraftId(result.agreement_id);
      }

      toast.success(`✅ Agreement "${draftName}" saved successfully! You can continue editing anytime.`);
      localStorage.setItem('last_draft_id', result.agreement_id);
      setShowSaveAsDialog(false);
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Error saving agreement draft: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const validateStep = () => {
    const stepErrors = [];

    if (currentStep === 1) {
      // Residential: Licensor or Commercial: Lessor validation
      if (formData.property_type !== 'commercial') {
        // Licensor phone validation
        if (!formData.licensor_phone) stepErrors.push('Licensor phone number is required');
        else {
          const phoneValidation = validatePhoneNumber(formData.licensor_phone);
          if (!phoneValidation.valid) stepErrors.push(`Licensor phone: ${phoneValidation.message}`);
        }
        // Licensor PAN validation
        if (formData.licensor_pan_aadhaar) {
          const panValidation = validatePAN(formData.licensor_pan_aadhaar);
          if (!panValidation.valid) stepErrors.push(`Licensor PAN: ${panValidation.message}`);
        }
      } else {
        // Lessor phone validation
        if (!formData.lessor_phone) stepErrors.push('Lessor phone number is required');
        else {
          const phoneValidation = validatePhoneNumber(formData.lessor_phone);
          if (!phoneValidation.valid) stepErrors.push(`Lessor phone: ${phoneValidation.message}`);
        }
        // Lessor PAN validation
        if (formData.lessor_pan_aadhaar) {
          const panValidation = validatePAN(formData.lessor_pan_aadhaar);
          if (!panValidation.valid) stepErrors.push(`Lessor PAN: ${panValidation.message}`);
        }
      }
    }

    if (currentStep === 2) {
      // Residential: Licensee or Commercial: Lessee validation
      if (formData.property_type !== 'commercial') {
        // Licensee phone validation
        if (!formData.licensee_phone) stepErrors.push('Licensee phone number is required');
        else {
          const phoneValidation = validatePhoneNumber(formData.licensee_phone);
          if (!phoneValidation.valid) stepErrors.push(`Licensee phone: ${phoneValidation.message}`);
        }
        // Licensee PAN validation
        if (formData.licensee_pan_aadhaar) {
          const panValidation = validatePAN(formData.licensee_pan_aadhaar);
          if (!panValidation.valid) stepErrors.push(`Licensee PAN: ${panValidation.message}`);
        }
      } else {
        // Lessee phone validation
        if (!formData.lessee_phone) stepErrors.push('Lessee phone number is required');
        else {
          const phoneValidation = validatePhoneNumber(formData.lessee_phone);
          if (!phoneValidation.valid) stepErrors.push(`Lessee phone: ${phoneValidation.message}`);
        }
        // Lessee PAN validation
        if (formData.lessee_pan_aadhaar) {
          const panValidation = validatePAN(formData.lessee_pan_aadhaar);
          if (!panValidation.valid) stepErrors.push(`Lessee PAN: ${panValidation.message}`);
        }
      }
    }

    return stepErrors;
  };

  const renderStep = () => {
    // PRODUCTION RULE: Skip Step 7 for creators (brokers, agents, self-managed landlords)
    // Step 7 shows payment breakdown which is only relevant for tenants and non-creator landlords
    if (currentStep === 7 && isCreator) {
      return null;  // Auto-skip this step
    }

    switch(currentStep) {
      case -1:
        return (
          <div className="step-content">
            <h2 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>{t('page_select_state')}</h2>
            <p style={{ color: '#a3a3a3', marginBottom: '2rem', fontSize: '1rem' }}>
              {t('page_select_state_desc')}
            </p>

            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontWeight: 600,
              color: '#f5f5f5',
              fontSize: '1rem'
            }}>
              {t('page_select_state_label')}
            </label>

            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '8px',
                border: '2px solid #F97316',
                background: '#1a1a1a',
                color: '#f5f5f5',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '2rem'
              }}
            >
              {STATES.map(state => (
                <option key={state} value={state}>
                  {state === 'Maharashtra' ? '🏢 Maharashtra (Default)' : `${state}`}
                </option>
              ))}
            </select>
          </div>
        );

      case 0:
        return (
          <div className="step-content">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{t('form_step_0_title')}</h2>
            <p style={{ color: '#a3a3a3', marginBottom: '1.5rem', fontSize: '0.95rem' }}>{t('form_step_0_description')}</p>

            {/* Agreement Type Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 600, color: '#f5f5f5', fontSize: '0.95rem' }}>
                📋 Agreement Format
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { value: 'e_stamp', labelKey: 'form_estamped_title', descKey: 'form_estamped_desc', disclaimerKey: 'form_estamped_benefit' },
                  { value: 'e_agreement', labelKey: 'form_eagreement_title', descKey: 'form_eagreement_desc', disclaimerKey: 'form_eagreement_benefit' },
                  { value: 'traditional_paper', labelKey: 'form_traditional_title', descKey: 'form_traditional_desc', disclaimerKey: 'form_traditional_benefit' }
                ].map(option => (
                  <div
                    key={option.value}
                    onClick={() => setAgreementType(option.value)}
                    style={{
                      padding: '1.25rem',
                      borderRadius: '10px',
                      border: `2px solid ${agreementType === option.value ? '#F97316' : '#444'}`,
                      background: agreementType === option.value ? 'rgba(249,115,22,0.12)' : '#2a2a2a',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: agreementType === option.value ? '0 0 0 1px rgba(249,115,22,0.3)' : 'none'
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                      {agreementType === option.value && '✓ '} {t(option.labelKey)}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#a3a3a3', marginBottom: '0.75rem' }}>{t(option.descKey)}</div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: option.value === 'e_stamp' ? '#6ee7b7' : option.value === 'e_agreement' ? '#f59e0b' : '#9ca3af',
                      fontWeight: 500
                    }}>
                      {t(option.disclaimerKey)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* E-Agreement Disclaimer */}
            {agreementType === 'e_agreement' && (
              <div style={{
                padding: '1rem',
                borderRadius: '8px',
                background: 'rgba(245, 158, 11, 0.08)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: 600 }}>ℹ️ Best For</div>
                <p style={{ fontSize: '0.8rem', color: '#fcd34d', margin: '0.5rem 0 0 0', lineHeight: 1.5 }}>
                  <strong>E-Agreement</strong> is suitable for short-term, informal arrangements <strong>under 11 months</strong> where both parties have a mutual understanding. It is <strong>NOT legally court-valid</strong> for dispute resolution. <strong>Recommend E-Stamp if you anticipate disputes or need long-term protection.</strong>
                </p>
              </div>
            )}

            {/* Commission Disclaimer for Agents/Brokers */}
            {agreementType && (
              <div style={{
                padding: '1rem',
                borderRadius: '8px',
                background: 'rgba(249, 115, 22, 0.08)',
                border: '1px solid rgba(249, 115, 22, 0.2)',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.85rem', color: '#F97316', fontWeight: 600, marginBottom: '0.75rem' }}>💰 Your Commission</div>
                {agreementType === 'e_stamp' && (
                  <p style={{ fontSize: '0.8rem', color: '#fbbf24', margin: 0, lineHeight: 1.6 }}>
                    <strong>Earn commission flat ₹50/-</strong> on this agreement<br/>
                    <span style={{ fontSize: '0.75rem', color: '#a3a3a3' }}>+ Additional commission on deposit protection (shown in Step 4)</span>
                  </p>
                )}
                {agreementType === 'e_agreement' && (
                  <p style={{ fontSize: '0.8rem', color: '#fbbf24', margin: 0, lineHeight: 1.6 }}>
                    <strong>Earn commission flat ₹25/-</strong> on this agreement<br/>
                    <span style={{ fontSize: '0.75rem', color: '#a3a3a3' }}>+ Additional commission on deposit protection (shown in Step 4)</span>
                  </p>
                )}
                {agreementType === 'traditional_paper' && (
                  <p style={{ fontSize: '0.8rem', color: '#fbbf24', margin: 0, lineHeight: 1.6 }}>
                    <strong>Commission depends on what you add below</strong><br/>
                    <span style={{ fontSize: '0.75rem', color: '#a3a3a3' }}>Your own agreement — no agreement fee. Add deposit protection to earn a commission on the protection fee, or the ₹1,299 dispute-resolution add-on to earn a flat ₹100. Choose neither and Orello isn't involved after signing.</span>
                  </p>
                )}
              </div>
            )}

            {/* Deposit Protection Selection — offered for every agreement type.
                Traditional paper included: the broker's own agreement can still
                use Orello for the deposit, or skip it and take the paid
                dispute-resolution add-on instead. */}
            {agreementType && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600, color: '#f5f5f5', fontSize: '0.95rem' }}>
                  🛡️ Include Deposit Protection?
                </label>
                <p style={{ fontSize: '0.85rem', color: '#a3a3a3', marginBottom: '1rem' }}>
                  Orello holds the security deposit in a protected nodal account and handles disputes, deduction claims, and settlement—keeping both parties safe.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {[
                    { value: true, label: 'Yes, protect the deposit', desc: '' },
                    { value: false, label: 'Agreement only', desc: 'No platform involvement' }
                  ].map(option => (
                    <div
                      key={String(option.value)}
                      onClick={() => {
                        setDepositProtection(option.value);
                        // Dispute-resolution add-on only applies when protection is declined.
                        if (option.value === true) setProofService(false);
                      }}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        borderRadius: '8px',
                        border: `2px solid ${depositProtection === option.value ? '#F97316' : '#444'}`,
                        background: depositProtection === option.value ? 'rgba(249,115,22,0.12)' : '#2a2a2a',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontWeight: 600, color: '#f5f5f5', fontSize: '0.9rem' }}>
                        {depositProtection === option.value && '✓ '} {option.label}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#a3a3a3', marginTop: '0.25rem' }}>{option.desc}</div>
                    </div>
                  ))}
                </div>

                {/* Dispute Resolution add-on — offered only when deposit protection is declined */}
                {depositProtection === false && (
                  <div
                    onClick={() => setProofService(prev => !prev)}
                    style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: `2px solid ${proofService ? '#F97316' : '#444'}`,
                      background: proofService ? 'rgba(249,115,22,0.12)' : '#2a2a2a',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#f5f5f5', fontSize: '0.9rem' }}>
                          {proofService && '✓ '}⚖️ Add Dispute Resolution by Orello
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#a3a3a3', marginTop: '0.35rem', lineHeight: 1.5 }}>
                          Orello holds no deposit, but handles disputes with condition reports and a clear proof trail — so both parties stay protected.
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 700, color: '#F97316', fontSize: '1rem' }}>₹1,299</div>
                        <div style={{ fontSize: '0.7rem', color: '#a3a3a3' }}>one-time</div>
                      </div>
                    </div>
                    {isBrokerOrAgent && (
                      <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 600, marginTop: '0.6rem' }}>
                        💰 Earn a flat ₹100 commission when this add-on is opted
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 0.5: {
        // Fee-payer selection — shown for all fee-bearing agreement types.
        // Traditional paper has no agreement fee of its own; it only reaches
        // this step when the ₹1,299 dispute-resolution add-on is taken.
        const isEStamp = agreementType === 'e_stamp';
        const isTraditional = agreementType === 'traditional_paper';
        const feeLabel = isTraditional
          ? 'the dispute-resolution fee'
          : isEStamp ? 'stamp duty & e-stamp fee' : 'the e-agreement fee';
        return (
          <div className="step-content">
            <h2>Who Pays the {isTraditional ? 'Fee' : 'Agreement Fee'}?</h2>
            <p style={{ color: '#a3a3a3', marginBottom: '1.5rem' }}>
              Choose who bears {feeLabel}. This determines who is asked to pay when signing the agreement.
            </p>

            {/* Fee breakdown — shown when the dispute-resolution add-on was taken
                (only possible when deposit protection is declined). For e-stamp /
                e-agreement the ₹1,299 is bundled on top of the agreement fee; for
                traditional paper there is no agreement fee, so it stands alone. */}
            {depositProtection === false && proofService && (
              <div style={{
                padding: '1rem',
                borderRadius: '8px',
                background: 'rgba(249, 115, 22, 0.08)',
                border: '1px solid rgba(249, 115, 22, 0.2)',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.85rem', color: '#F97316', fontWeight: 600, marginBottom: '0.75rem' }}>🧾 Fee Breakdown</div>
                {!isTraditional && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d1d5db', fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                    <span>{isEStamp ? 'E-Stamp fee' : 'E-Agreement fee'}</span>
                    <span style={{ color: '#fbbf24' }}>₹{isEStamp ? '299 + stamp duty' : '99'}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d1d5db', fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                  <span>Dispute Resolution by Orello</span>
                  <span style={{ color: '#fbbf24' }}>₹1,299</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '1px solid rgba(249,115,22,0.25)', fontWeight: 700, color: '#f5f5f5', fontSize: '0.9rem' }}>
                  <span>Total {feePayer === 'split' ? '(split 50-50)' : ''}</span>
                  <span style={{ color: '#10b981' }}>₹{isTraditional ? '1,299' : isEStamp ? '1,598 + stamp duty' : '1,398'}</span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                { value: 'tenant', label: 'Tenant pays full amount', desc: `Tenant pays ${feeLabel}` },
                { value: 'landlord', label: 'Landlord pays full amount', desc: `Landlord pays ${feeLabel}` },
                { value: 'split', label: 'Split 50-50', desc: `Tenant and landlord each pay half of ${feeLabel}` }
              ].map(option => (
                <div
                  key={option.value}
                  onClick={() => setFeePayer(option.value)}
                  style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    border: `2px solid ${feePayer === option.value ? '#F97316' : '#444'}`,
                    background: feePayer === option.value ? 'rgba(249,115,22,0.1)' : '#2a2a2a',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#f5f5f5', marginBottom: '0.25rem' }}>
                    {feePayer === option.value && '✓ '} {option.label}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#a3a3a3' }}>{option.desc}</div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 1:
        const isCommercialStep1 = formData.property_type === 'commercial';
        const phoneStep1 = isCommercialStep1 ? formData.lessor_phone : formData.licensor_phone;
        const phoneValidationStep1 = phoneStep1 ? validatePhoneNumber(phoneStep1) : { valid: false, message: '' };
        const panStep1 = isCommercialStep1 ? formData.lessor_pan_aadhaar : formData.licensor_pan_aadhaar;
        const panValidationStep1 = panStep1 ? validatePAN(panStep1) : { valid: true, message: '' };

        return (
          <div className="step-content">
            <h2>{t('form_step_1_title')}</h2>

            {/* Property Verification Disclaimer */}
            <div style={{
              padding: '0.75rem',
              borderRadius: '6px',
              background: 'rgba(251, 146, 60, 0.1)',
              border: '1px solid rgba(251, 146, 60, 0.3)',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: '#fb923c', fontWeight: 600 }}>ⓘ Important</div>
              <p style={{ fontSize: '0.8rem', color: '#fdba74', margin: '0.5rem 0 0 0' }}>
                Orello does not verify property ownership or legitimacy. All parties are responsible for due diligence regarding the property.
              </p>
            </div>

            {!isCommercialStep1 ? (
              <>
                <input type="text" name="licensor_full_name" placeholder="Full Name" value={formData.licensor_full_name} onChange={handleInputChange} />
                <input type="number" name="licensor_age" placeholder="Age" value={formData.licensor_age} onChange={handleInputChange} />
                <input type="text" name="licensor_fathers_name" placeholder="Father's/Spouse's Name" value={formData.licensor_fathers_name} onChange={handleInputChange} />
                <textarea name="licensor_address" placeholder="Full Address" value={formData.licensor_address} onChange={handleInputChange} />

                {/* PAN Number with validation */}
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="licensor_pan_aadhaar"
                    placeholder="PAN: AAAAA NNNN C (e.g., ABCDE1234F)"
                    value={formData.licensor_pan_aadhaar}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      border: `2px solid ${formData.licensor_pan_aadhaar && !panValidationStep1.valid ? '#ef4444' : '#444'}`,
                      borderRadius: '6px',
                      background: '#1a1a1a',
                      color: '#f5f5f5',
                      padding: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {formData.licensor_pan_aadhaar && !panValidationStep1.valid && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      marginTop: '-0.5rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ❌ {panValidationStep1.message}
                    </div>
                  )}
                </div>

                {/* Phone Number with +91 prefix */}
                <div style={{ position: 'relative' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `2px solid ${!phoneValidationStep1.valid && formData.licensor_phone ? '#ef4444' : '#444'}`,
                    borderRadius: '6px',
                    background: '#1a1a1a',
                    padding: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ color: '#888', fontWeight: 600, marginRight: '0.5rem' }}>+91</span>
                    <input
                      type="tel"
                      name="licensor_phone"
                      placeholder="10-digit phone number"
                      value={formData.licensor_phone}
                      onChange={handleInputChange}
                      maxLength="10"
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        color: '#f5f5f5',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  {formData.licensor_phone && !phoneValidationStep1.valid && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      marginTop: '-0.5rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ❌ {phoneValidationStep1.message}
                    </div>
                  )}
                </div>

                <input type="email" name="licensor_email" placeholder="Email" value={formData.licensor_email} onChange={handleInputChange} />
              </>
            ) : (
              // Commercial: Lessor fields
              <>
                <input type="text" name="lessor_full_name" placeholder="Full Name" value={formData.lessor_full_name || ''} onChange={handleInputChange} />
                <input type="number" name="lessor_age" placeholder="Age (18-100)" value={formData.lessor_age || ''} onChange={handleInputChange} />
                <input type="text" name="lessor_fathers_name" placeholder="Father's/Spouse's Name" value={formData.lessor_fathers_name || ''} onChange={handleInputChange} />
                <textarea name="lessor_address" placeholder="Full Address" value={formData.lessor_address || ''} onChange={handleInputChange} />

                {/* PAN Number with validation */}
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="lessor_pan_aadhaar"
                    placeholder="PAN: AAAAA NNNN C (e.g., ABCDE1234F)"
                    value={formData.lessor_pan_aadhaar || ''}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      border: `2px solid ${formData.lessor_pan_aadhaar && !panValidationStep1.valid ? '#ef4444' : '#444'}`,
                      borderRadius: '6px',
                      background: '#1a1a1a',
                      color: '#f5f5f5',
                      padding: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {formData.lessor_pan_aadhaar && !panValidationStep1.valid && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      marginTop: '-0.5rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ❌ {panValidationStep1.message}
                    </div>
                  )}
                </div>

                {/* Phone Number with +91 prefix */}
                <div style={{ position: 'relative' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `2px solid ${!phoneValidationStep1.valid && formData.lessor_phone ? '#ef4444' : '#444'}`,
                    borderRadius: '6px',
                    background: '#1a1a1a',
                    padding: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ color: '#888', fontWeight: 600, marginRight: '0.5rem' }}>+91</span>
                    <input
                      type="tel"
                      name="lessor_phone"
                      placeholder="10-digit phone number"
                      value={formData.lessor_phone || ''}
                      onChange={handleInputChange}
                      maxLength="10"
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        color: '#f5f5f5',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  {formData.lessor_phone && !phoneValidationStep1.valid && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      marginTop: '-0.5rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ❌ {phoneValidationStep1.message}
                    </div>
                  )}
                </div>

                <input type="email" name="lessor_email" placeholder="Email" value={formData.lessor_email || ''} onChange={handleInputChange} />
              </>
            )}
          </div>
        );

      case 2:
        const isCommercialStep2 = formData.property_type === 'commercial';
        const phoneStep2 = isCommercialStep2 ? formData.lessee_phone : formData.licensee_phone;
        const phoneValidationStep2 = phoneStep2 ? validatePhoneNumber(phoneStep2) : { valid: false, message: '' };
        const panStep2 = isCommercialStep2 ? formData.lessee_pan_aadhaar : formData.licensee_pan_aadhaar;
        const panValidationStep2 = panStep2 ? validatePAN(panStep2) : { valid: true, message: '' };

        return (
          <div className="step-content">
            <h2>{t('form_step_2_title')}</h2>

            {!isCommercialStep2 ? (
              <>
                <input type="text" name="licensee_full_name" placeholder="Full Name" value={formData.licensee_full_name} onChange={handleInputChange} />
                <input type="number" name="licensee_age" placeholder="Age" value={formData.licensee_age} onChange={handleInputChange} />
                <input type="text" name="licensee_fathers_name" placeholder="Father's/Spouse's Name" value={formData.licensee_fathers_name} onChange={handleInputChange} />
                <textarea name="licensee_address" placeholder="Full Address" value={formData.licensee_address} onChange={handleInputChange} />

                {/* PAN Number with validation */}
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="licensee_pan_aadhaar"
                    placeholder="PAN: AAAAA NNNN C (e.g., ABCDE1234F)"
                    value={formData.licensee_pan_aadhaar}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      border: `2px solid ${formData.licensee_pan_aadhaar && !panValidationStep2.valid ? '#ef4444' : '#444'}`,
                      borderRadius: '6px',
                      background: '#1a1a1a',
                      color: '#f5f5f5',
                      padding: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {formData.licensee_pan_aadhaar && !panValidationStep2.valid && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      marginTop: '-0.5rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ❌ {panValidationStep2.message}
                    </div>
                  )}
                </div>

                {/* Phone Number with +91 prefix */}
                <div style={{ position: 'relative' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `2px solid ${!phoneValidationStep2.valid && formData.licensee_phone ? '#ef4444' : '#444'}`,
                    borderRadius: '6px',
                    background: '#1a1a1a',
                    padding: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ color: '#888', fontWeight: 600, marginRight: '0.5rem' }}>+91</span>
                    <input
                      type="tel"
                      name="licensee_phone"
                      placeholder="10-digit phone number"
                      value={formData.licensee_phone}
                      onChange={handleInputChange}
                      maxLength="10"
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        color: '#f5f5f5',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  {formData.licensee_phone && !phoneValidationStep2.valid && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      marginTop: '-0.5rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ❌ {phoneValidationStep2.message}
                    </div>
                  )}
                </div>

                <input type="email" name="licensee_email" placeholder="Email" value={formData.licensee_email} onChange={handleInputChange} />
              </>
            ) : (
              // Commercial: Lessee fields
              <>
                <input type="text" name="lessee_full_name" placeholder="Full Name" value={formData.lessee_full_name || ''} onChange={handleInputChange} />
                <input type="number" name="lessee_age" placeholder="Age (18-100)" value={formData.lessee_age || ''} onChange={handleInputChange} />
                <input type="text" name="lessee_fathers_name" placeholder="Father's/Spouse's Name" value={formData.lessee_fathers_name || ''} onChange={handleInputChange} />
                <textarea name="lessee_address" placeholder="Full Address" value={formData.lessee_address || ''} onChange={handleInputChange} />

                {/* PAN Number with validation */}
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="lessee_pan_aadhaar"
                    placeholder="PAN: AAAAA NNNN C (e.g., ABCDE1234F)"
                    value={formData.lessee_pan_aadhaar || ''}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      border: `2px solid ${formData.lessee_pan_aadhaar && !panValidationStep2.valid ? '#ef4444' : '#444'}`,
                      borderRadius: '6px',
                      background: '#1a1a1a',
                      color: '#f5f5f5',
                      padding: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {formData.lessee_pan_aadhaar && !panValidationStep2.valid && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      marginTop: '-0.5rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ❌ {panValidationStep2.message}
                    </div>
                  )}
                </div>

                {/* Phone Number with +91 prefix */}
                <div style={{ position: 'relative' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `2px solid ${!phoneValidationStep2.valid && formData.lessee_phone ? '#ef4444' : '#444'}`,
                    borderRadius: '6px',
                    background: '#1a1a1a',
                    padding: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ color: '#888', fontWeight: 600, marginRight: '0.5rem' }}>+91</span>
                    <input
                      type="tel"
                      name="lessee_phone"
                      placeholder="10-digit phone number"
                      value={formData.lessee_phone || ''}
                      onChange={handleInputChange}
                      maxLength="10"
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        color: '#f5f5f5',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  {formData.lessee_phone && !phoneValidationStep2.valid && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      marginTop: '-0.5rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ❌ {phoneValidationStep2.message}
                    </div>
                  )}
                </div>

                <input type="email" name="lessee_email" placeholder="Email" value={formData.lessee_email || ''} onChange={handleInputChange} />
              </>
            )}
          </div>
        );

      case 3:
        // MERGED Step 3 & 5: Property Details + Lease Duration
        const isCommercial3 = formData.property_type === 'commercial';
        const isResidential3 = ['flat', 'house', 'villa', 'studio'].includes(formData.property_type);
        const leaseYears3 = parseInt(formData.lease_duration_years) || 0;
        const leaseMonths3 = parseInt(formData.lease_duration_months) || 0;
        const selectedDurationMonths3 = leaseYears3 * 12 + leaseMonths3;

        // Calculate actual duration from start and end dates
        const calculateDurationFromDates3 = () => {
          if (!formData.start_date || !formData.end_date) return null;
          try {
            const startDate = new Date(formData.start_date);
            const endDate = new Date(formData.end_date);
            const diffTime = endDate - startDate;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            const diffMonths = Math.round(diffDays / 30.44);
            return Math.max(0, diffMonths);
          } catch (e) {
            return null;
          }
        };

        const actualDurationMonths3 = calculateDurationFromDates3();
        const totalLeaseDurationMonths3 = actualDurationMonths3 !== null ? actualDurationMonths3 : selectedDurationMonths3;

        const formatLeaseDuration3 = () => {
          const years = Math.floor(totalLeaseDurationMonths3 / 12);
          const months = totalLeaseDurationMonths3 % 12;
          if (years > 0 && months > 0) return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
          if (years > 0) return `${years} year${years > 1 ? 's' : ''}`;
          return `${months} month${months > 1 ? 's' : ''}`;
        };

        return (
          <div className="step-content">
            <h2>Step 3: Property Details & Lease Duration</h2>

            {/* ========== SECTION 1: PROPERTY DETAILS ========== */}
            <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '2px solid #444' }}>
              <h3 style={{ color: '#F97316', marginBottom: '1rem', fontSize: '1.1rem' }}>📍 Property Details</h3>

              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Property Type</label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleInputChange}
                style={{ marginBottom: '1.5rem' }}
              >
                <option value="flat">Flat (Residential)</option>
                <option value="house">House (Residential)</option>
                <option value="villa">Villa (Residential)</option>
                <option value="studio">Studio (Residential)</option>
                <option value="commercial">Commercial Property</option>
              </select>

              {/* Residential Property Fields - State Specific */}
              {isResidential3 && (
                <ResidentialPropertyFields
                  formData={formData}
                  handleInputChange={handleInputChange}
                  STATES={STATES}
                  depositLocked={!!renewalOfRentalId}
                />
              )}

              {/* Commercial Property Fields - State Specific */}
              {isCommercial3 && (
                <CommercialPropertyFields
                  formData={formData}
                  handleInputChange={handleInputChange}
                  STATES={STATES}
                  depositLocked={!!renewalOfRentalId}
                />
              )}
            </div>

            {/* ========== SECTION 2: LEASE DURATION & TERMS ========== */}
            <div>
              <h3 style={{ color: '#F97316', marginBottom: '1rem', fontSize: '1.1rem' }}>📅 Lease Terms & Duration</h3>

              {/* MANUAL INPUT: Lease Start Date */}
              <label style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 600 }}>
                📅 Lease Start Date: <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                style={{ marginBottom: '1.5rem' }}
              />

              {/* MANUAL INPUT: Lease End Date */}
              <label style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 600 }}>
                📅 Lease End Date: <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                style={{ marginBottom: '1.5rem' }}
              />

              {/* AUTO-CALCULATED: Lease Duration Display */}
              {formData.start_date && formData.end_date && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem'
                  }}>
                    {/* Auto-calculated Years */}
                    <div style={{
                      padding: '1rem',
                      borderRadius: '8px',
                      background: 'rgba(34, 197, 94, 0.08)',
                      border: '1px solid rgba(34, 197, 94, 0.3)'
                    }}>
                      <label style={{ fontSize: '0.85rem', color: '#22c55e', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                        Lease Duration (Years) - Auto-calculated
                      </label>
                      <p style={{ margin: 0, color: '#f5f5f5', fontSize: '1.1rem', fontWeight: 600 }}>
                        {(() => {
                          const start = new Date(formData.start_date);
                          const end = new Date(formData.end_date);
                          const diffTime = end - start;
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          const totalMonths = Math.round(diffDays / 30.44);
                          const years = Math.floor(totalMonths / 12);
                          return years;
                        })()} year{(() => {
                          const start = new Date(formData.start_date);
                          const end = new Date(formData.end_date);
                          const diffTime = end - start;
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          const totalMonths = Math.round(diffDays / 30.44);
                          const years = Math.floor(totalMonths / 12);
                          return years !== 1 ? 's' : '';
                        })()}
                      </p>
                    </div>

                    {/* Auto-calculated Remaining Months (after years) */}
                    <div style={{
                      padding: '1rem',
                      borderRadius: '8px',
                      background: 'rgba(34, 197, 94, 0.08)',
                      border: '1px solid rgba(34, 197, 94, 0.3)'
                    }}>
                      <label style={{ fontSize: '0.85rem', color: '#22c55e', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                        Lease Duration (Months) - Auto-calculated
                      </label>
                      <p style={{ margin: 0, color: '#f5f5f5', fontSize: '1.1rem', fontWeight: 600 }}>
                        {(() => {
                          const start = new Date(formData.start_date);
                          const end = new Date(formData.end_date);
                          const diffTime = end - start;
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          const totalMonths = Math.round(diffDays / 30.44);
                          const months = totalMonths % 12;
                          return months;
                        })()} month{(() => {
                          const start = new Date(formData.start_date);
                          const end = new Date(formData.end_date);
                          const diffTime = end - start;
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          const totalMonths = Math.round(diffDays / 30.44);
                          const months = totalMonths % 12;
                          return months !== 1 ? 's' : '';
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* LEASE PERIOD SUMMARY */}
              <div style={{
                padding: '1rem',
                background: 'rgba(249, 115, 22, 0.08)',
                border: '1px solid rgba(249, 115, 22, 0.2)',
                borderRadius: '6px',
                marginBottom: '1.5rem'
              }}>
                <p style={{ margin: '0 0 0.5rem 0', color: '#F97316', fontWeight: 600, fontSize: '0.9rem' }}>📅 Lease Period Summary (Auto-Calculated):</p>
                <p style={{ margin: 0, color: '#f5f5f5', fontSize: '0.9rem', lineHeight: 1.6 }}>
                  <strong>Start:</strong> {formData.start_date ? new Date(formData.start_date).toLocaleDateString() : 'Not set'} <br/>
                  <strong>End:</strong> {formData.end_date ? new Date(formData.end_date).toLocaleDateString() : 'Not set'} <br/>
                  {formData.start_date && formData.end_date && (
                    <strong>Total Duration:</strong>
                  )}
                  {formData.start_date && formData.end_date && (
                    <span style={{ marginLeft: '0.5rem', color: '#22c55e' }}>
                      {(() => {
                        const start = new Date(formData.start_date);
                        const end = new Date(formData.end_date);
                        const diffTime = end - start;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        const totalMonths = Math.round(diffDays / 30.44);
                        const years = Math.floor(totalMonths / 12);
                        const months = totalMonths % 12;

                        if (years > 0 && months > 0) return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
                        if (years > 0) return `${years} year${years > 1 ? 's' : ''}`;
                        return `${months} month${months > 1 ? 's' : ''}`;
                      })()}
                    </span>
                  )}
                </p>
              </div>

              {/* OPTIONAL: Lock-in Period */}
              <label style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 600 }}>
                🔒 Lock-in Period (months) - <span style={{ color: '#888', fontSize: '0.9rem', fontWeight: 400 }}>Optional</span>
              </label>
              <input
                type="number"
                name="lock_in_months"
                placeholder="0 for no lock-in, or enter months"
                value={formData.lock_in_months}
                onChange={handleInputChange}
                min="0"
                style={{ marginBottom: '0.5rem' }}
              />
              <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                ℹ️ Tenant cannot exit before this period ends. Leave blank or enter 0 if no lock-in period.
              </p>
            </div>
          </div>
        );

      case 4:
        // Step 4: Financial Details (NO Commission - moved to Step 7)
        return (
          <div className="step-content">
            <h2>Step 4: Financial Details</h2>

            <label>Monthly Rent (₹):</label>
            <input type="number" name="monthly_rent" placeholder="Enter monthly rent" value={formData.monthly_rent} onChange={handleInputChange} />

            <label style={{ marginTop: '1rem' }}>Security Deposit (₹):</label>
            <input type="number" name="security_deposit" placeholder="Enter deposit amount" value={formData.security_deposit} onChange={handleInputChange} disabled={!!renewalOfRentalId} title={renewalOfRentalId ? "Deposit carries over from your current tenancy — cannot be changed at renewal" : undefined} />
            {renewalOfRentalId && <p style={{ color: '#a3a3a3', fontSize: '0.85rem', marginTop: '0.25rem' }}>Deposit carries over from your current tenancy — cannot be changed at renewal.</p>}

            <label style={{ marginTop: '1rem' }}>Maintenance Charges (₹/month):</label>
            <input type="number" name="maintenance_charges" placeholder="Monthly maintenance (₹)" value={formData.maintenance_charges} onChange={handleInputChange} />

            <label style={{ marginTop: '1rem' }}>Annual Escalation (%):</label>
            <input type="number" name="escalation_percent" placeholder="e.g., 5% per year" value={formData.escalation_percent} onChange={handleInputChange} />

            <label style={{ marginTop: '1rem' }}>Late Fee (₹/day):</label>
            <input type="number" name="late_fee" placeholder="Late payment fee per day" value={formData.late_fee} onChange={handleInputChange} />

            <label style={{ marginTop: '1rem' }}>Electricity Payer:</label>
            <select name="electricity_payer" value={formData.electricity_payer} onChange={handleInputChange}>
              <option value="">Select option</option>
              <option value="tenant">Electricity: Tenant pays</option>
              <option value="landlord">Electricity: Landlord pays</option>
            </select>

            <label style={{ marginTop: '1rem' }}>Water Payer:</label>
            <select name="water_payer" value={formData.water_payer} onChange={handleInputChange}>
              <option value="">Select option</option>
              <option value="tenant">Water: Tenant pays</option>
              <option value="landlord">Water: Landlord pays</option>
            </select>
          </div>
        );

      case 5:
        // Step 5: Amenities & Policies (was Step 6)
        return (
          <div className="step-content">
            <h2>Step 5: Amenities & Policies</h2>
            <input type="number" name="parking_spaces" placeholder="Parking Spaces" value={formData.parking_spaces} onChange={handleInputChange} />
            <select name="pets_allowed" value={formData.pets_allowed} onChange={handleInputChange}>
              <option value="no">Pets: Not Allowed</option>
              <option value="yes">Pets: Allowed</option>
              <option value="with_restrictions">Pets: With Restrictions</option>
            </select>
          </div>
        );

      case 6:
        // Step 6: Additional Details (was Step 7)
        return (
          <div className="step-content">
            <h2>Step 6: Additional Details</h2>
            <input type="text" name="jurisdiction_city" placeholder="Jurisdiction City" value={formData.jurisdiction_city} onChange={handleInputChange} />
            <input type="text" name="broker_name" placeholder="Broker Name (optional)" value={formData.broker_name} onChange={handleInputChange} />
            <input type="text" name="broker_license" placeholder="Broker License (optional)" value={formData.broker_license} onChange={handleInputChange} />
          </div>
        );

      case 7:
        // Step 7: Charges & Commission (moved from Step 4)
        return (
          <div className="step-content">
            <h2>Step 7: Charges & Your Commission</h2>

            {(() => {
              const depositAmount = parseFloat(formData.security_deposit) || 0;
              let feePercentage = 0;
              if (depositAmount <= 150000) feePercentage = 0.5;
              else if (depositAmount <= 1500000) feePercentage = 0.7;
              else feePercentage = 1;
              const agentCommissionFlat = agreementType === 'e_stamp' ? 50 : agreementType === 'e_agreement' ? 25 : 0;
              const depositFeeAmount = (depositAmount * feePercentage) / 100;
              const agentDepositCommission = (depositFeeAmount * 15) / 100;
              const totalCommission = agentCommissionFlat + agentDepositCommission;

              return depositAmount > 0 && (agreementType === 'e_stamp' || agreementType === 'e_agreement') && depositProtection ? (
              <div style={{
                marginTop: '0',
                padding: '1.25rem',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(249, 115, 22, 0.1))',
                border: '1px solid rgba(249, 115, 22, 0.2)'
              }}>
                <div style={{ fontSize: '0.95rem', color: '#F97316', fontWeight: 600, marginBottom: '1rem' }}>
                  💰 Charges & Your Commission on This Deal
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.9rem' }}>
                  {/* Agreement Fee Line */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d1d5db', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(249, 115, 22, 0.2)' }}>
                    <span>Agreement Fee ({agreementType === 'e_stamp' ? 'E-Stamp' : 'E-Agreement'}, paid by tenant)</span>
                    <span style={{ color: '#fbbf24', fontWeight: 600 }}>₹{agreementType === 'e_stamp' ? '299 + stamp duty' : '99'}</span>
                  </div>

                  {/* Deposit Protection Fee */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d1d5db', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(249, 115, 22, 0.2)' }}>
                    <span>One time Deposit protection fee for ₹{depositAmount.toLocaleString()} (tenant pays)</span>
                    <span style={{ color: '#fbbf24', fontWeight: 600 }}>₹{depositFeeAmount.toFixed(2)}</span>
                  </div>

                  {/* Your Commission Breakdown */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingLeft: '1rem', color: '#a3a3a3', fontSize: '0.85rem' }}>
                    <div><strong style={{color: '#f5f5f5'}}>Your Commission Breakdown:</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Agreement commission from broker</span>
                      <span style={{ color: '#fbbf24' }}>₹{agentCommissionFlat.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Your commission on deposit protection</span>
                      <span style={{ color: '#fbbf24' }}>₹{agentDepositCommission.toFixed(2)}</span>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '1rem',
                    borderTop: '1px solid rgba(249, 115, 22, 0.3)',
                    marginTop: '0.5rem',
                    fontSize: '1rem',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '6px'
                  }}>
                    <span style={{ fontWeight: 600, color: '#f5f5f5' }}>💸 Your Total Commission:</span>
                    <span style={{ fontWeight: 700, color: '#10b981', fontSize: '1.1rem' }}>₹{totalCommission.toFixed(2)}</span>
                  </div>
                </div>

                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '1rem', margin: '1rem 0 0 0' }}>
                  💡 <strong>Note:</strong> Your commission is earned when the tenant deposits money. It is 15% of the deposit platform fee excluding GST — 0.7% on the first ₹1,50,000 of the deposit and 1% above that, min ₹250, max ₹25,000.
                </p>
              </div>
              ) : (
                <div style={{
                  padding: '1.5rem',
                  borderRadius: '8px',
                  background: 'rgba(100, 116, 139, 0.1)',
                  border: '1px solid rgba(100, 116, 139, 0.3)',
                  textAlign: 'center',
                  color: '#94a3b8'
                }}>
                  <p>No commission data available yet. Make sure:</p>
                  <ul style={{textAlign: 'left', display: 'inline-block', margin: '0.5rem 0'}}>
                    <li>✓ Security deposit is filled in Step 4</li>
                    <li>✓ Agreement type is E-Agreement or E-Stamp</li>
                    <li>✓ Deposit protection is enabled</li>
                  </ul>
                </div>
              );
            })()}
          </div>
        );

      case 8:
        return (
          <div className="step-content">
            <h2>{t('form_step_8_title')}</h2>

            {/* PDF PREVIEW & DOWNLOAD (Consolidated - Always Visible) */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{
                padding: '1rem 1.5rem',
                background: 'rgba(249, 115, 22, 0.1)',
                border: '1px solid rgba(249, 115, 22, 0.3)',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{ margin: 0, color: '#F97316', fontSize: '1rem', fontWeight: 600 }}>📄 Agreement Preview & Download</h3>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#fb923c', fontSize: '0.9rem' }}>
                    {agreementType === 'e_stamp'
                      ? 'This is the comprehensive E-Stamp agreement with government stamp duty, legal clauses, and jurisdiction-specific terms.'
                      : 'This is the comprehensive agreement with all legal clauses and jurisdiction-specific terms.'}
                  </p>
                </div>
              </div>
              <AgreementPDFPreview
                ref={pdfPreviewRef}
                formData={formData}
                agreementId={persistentAgreementId}
                agreementType={agreementType}
                propertyType={formData.property_type}
                previewOnly={false}
                userRole={user?.role}
                depositProtectionSelected={depositProtection}
                depositAmount={parseFloat(formData.security_deposit) || 0}
                feePayer={feePayer}
              />
            </div>

            {/* SEND INVITES SECTION */}
            <div>
              <h3 style={{ color: '#f5f5f5', marginBottom: '1rem', fontSize: '1.1rem' }}>📧 Share Agreement & Send Invites</h3>
              <p style={{ color: '#888', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                Before finalizing, check who needs to sign. Registered users can sign immediately. Unregistered users will receive an email/SMS invite to create an account and sign.
              </p>

                {/* Legal Validity Disclaimer */}
                {agreementType === 'e_agreement' && (
                  <div style={{
                    padding: '1rem',
                    borderRadius: '6px',
                    background: 'rgba(255, 140, 66, 0.1)',
                    border: '1px solid rgba(255, 140, 66, 0.3)',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.9rem', color: '#ff8c42', fontWeight: 700 }}>⚠️ E-Agreement Legal Notice</div>
                      <button
                        onClick={() => setAgreementType('e_stamp')}
                        style={{
                          padding: '0.4rem 0.8rem',
                          borderRadius: '4px',
                          background: '#22c55e',
                          color: '#fff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        ⬆️ Switch to E-Stamp
                      </button>
                    </div>
                    <ul style={{ fontSize: '0.8rem', color: '#ffaa66', margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                      <li>This is a casual handshake agreement - NOT court-valid</li>
                      <li>Best for lease terms under 11 months</li>
                      <li>For legal disputes, courts may not enforce this agreement</li>
                      <li>Click "Switch to E-Stamp" above for full legal protection (₹299 platform fee)</li>
                    </ul>
                  </div>
                )}

                {agreementType === 'e_stamp' && (
                  <div style={{
                    padding: '1rem',
                    borderRadius: '6px',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#22c55e', fontWeight: 700 }}>✓ E-Stamp Agreement</div>
                    <p style={{ fontSize: '0.8rem', color: '#86efac', margin: '0.5rem 0 0 0' }}>
                      This agreement is legally stamped and court-admissible for any lease duration.
                    </p>
                  </div>
                )}

                {agreementType === 'traditional_paper' && (
                  <div style={{
                    padding: '1rem',
                    borderRadius: '6px',
                    background: 'rgba(100, 116, 139, 0.1)',
                    border: '1px solid rgba(100, 116, 139, 0.3)',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 700 }}>📄 External Agreement</div>
                    <ul style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                      <li>Use your own agreement (created by broker/agent)</li>
                      <li>Agreement terms not created/stored in Orello</li>
                      <li>No Orello agreement fee</li>
                      {depositProtection === true && (
                        <li>Orello holds &amp; protects the deposit — fee 0.7% on the first ₹1,50,000, 1% above (min ₹250, max ₹25,000) + 18% GST</li>
                      )}
                      {depositProtection === false && proofService && (
                        <li>Dispute resolution by Orello — ₹1,299 one-time, no deposit held</li>
                      )}
                      {depositProtection === false && !proofService && (
                        <li>Orello is not involved after signing — no deposit held, no dispute resolution</li>
                      )}
                    </ul>
                  </div>
                )}

                <div style={{
                  padding: '0.75rem',
                  borderRadius: '6px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ fontSize: '0.85rem', color: '#22c55e', fontWeight: 600, marginBottom: '0.5rem' }}>📧 Receipt Confirmation</div>
                  <p style={{ fontSize: '0.8rem', color: '#86efac', margin: 0 }}>
                    After the tenant completes deposit payment, all parties will receive an email confirmation receipt with transaction details, deposit protection terms, and next steps.
                  </p>
                </div>

                <p style={{ marginBottom: '1.5rem', color: '#d1d1d1' }}>
                  Before finalizing, check who needs to sign this agreement and send them invitations if they're not yet registered on Orello.
                </p>

                {/* Licensor (Landlord) Registration Status */}
                <div style={{
                  padding: '1.25rem',
                  border: '1px solid rgba(249,115,22,0.3)',
                  borderRadius: '8px',
                  background: licensorStatus?.registered ? 'rgba(34,197,94,0.05)' : 'rgba(249,115,22,0.05)',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div>
                      <h4 style={{ color: '#f5f5f5', marginBottom: '0.25rem' }}>
                        🏠 Property Owner (Licensor)
                      </h4>
                      <p style={{ color: '#a3a3a3', fontSize: '0.85rem', margin: 0 }}>
                        {formData.licensor_full_name || 'Name not provided'}
                      </p>
                      <p style={{ color: '#888', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        📱 {formData.licensor_phone || 'Phone not provided'}
                      </p>
                    </div>
                    {licensorStatus ? (
                      <div style={{ textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.4rem 0.8rem',
                          borderRadius: '6px',
                          background: licensorStatus.registered ? '#10b981' : '#f97316',
                          color: '#fff',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          marginBottom: '0.5rem'
                        }}>
                          {licensorStatus.registered ? '✓ Registered' : '○ Not Registered'}
                        </span>
                        {licensorStatus.registered && (
                          <p style={{ color: '#22c55e', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                            ✓ Ready to sign
                          </p>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#888', fontSize: '0.85rem', fontStyle: 'italic' }}>Checking...</span>
                    )}
                  </div>
                  {licensorStatus && !licensorStatus.registered && (
                    <button
                      onClick={() => inviteLicensor()}
                      disabled={loading || !formData.licensor_phone || !formData.licensor_email}
                      title={(!formData.licensor_phone || !formData.licensor_email) ? 'Phone and email required' : ''}
                      style={{
                        width: '100%',
                        padding: '0.6rem 1rem',
                        borderRadius: '6px',
                        background: (!formData.licensor_phone || !formData.licensor_email) ? '#555' : '#F97316',
                        color: '#fff',
                        border: 'none',
                        cursor: (!formData.licensor_phone || !formData.licensor_email) ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 600
                      }}
                    >
                      {loading ? '⏳ Sending Invite...' : '📧 Send Invite Link'}
                    </button>
                  )}
                </div>

                {/* Licensee (Tenant) Registration Status */}
                <div style={{
                  padding: '1.25rem',
                  border: '1px solid rgba(249,115,22,0.3)',
                  borderRadius: '8px',
                  background: licenseeStatus?.registered ? 'rgba(34,197,94,0.05)' : 'rgba(249,115,22,0.05)',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div>
                      <h4 style={{ color: '#f5f5f5', marginBottom: '0.25rem' }}>
                        👤 Tenant (Licensee)
                      </h4>
                      <p style={{ color: '#a3a3a3', fontSize: '0.85rem', margin: 0 }}>
                        {formData.licensee_full_name || 'Name not provided'}
                      </p>
                      <p style={{ color: '#888', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        📱 {formData.licensee_phone || 'Phone not provided'}
                      </p>
                    </div>
                    {licenseeStatus ? (
                      <div style={{ textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.4rem 0.8rem',
                          borderRadius: '6px',
                          background: licenseeStatus.registered ? '#10b981' : '#f97316',
                          color: '#fff',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          marginBottom: '0.5rem'
                        }}>
                          {licenseeStatus.registered ? '✓ Registered' : '○ Not Registered'}
                        </span>
                        {licenseeStatus.registered && (
                          <p style={{ color: '#22c55e', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                            ✓ Ready to sign
                          </p>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#888', fontSize: '0.85rem', fontStyle: 'italic' }}>Checking...</span>
                    )}
                  </div>
                  {licenseeStatus && !licenseeStatus.registered && (
                    <button
                      onClick={() => inviteLicensee()}
                      disabled={loading || !formData.licensee_phone || !formData.licensee_email}
                      title={(!formData.licensee_phone || !formData.licensee_email) ? 'Phone and email required' : ''}
                      style={{
                        width: '100%',
                        padding: '0.6rem 1rem',
                        borderRadius: '6px',
                        background: (!formData.licensee_phone || !formData.licensee_email) ? '#555' : '#F97316',
                        color: '#fff',
                        border: 'none',
                        cursor: (!formData.licensee_phone || !formData.licensee_email) ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 600
                      }}
                    >
                      {loading ? '⏳ Sending Invite...' : '📧 Send Invite Link'}
                    </button>
                  )}
                </div>

                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(249,115,22,0.2)' }}>
                  💡 <strong>Auto-Detection:</strong> The system automatically checks if phone numbers or emails are registered in Orello (either one qualifies). Registered users can sign immediately. Unregistered users receive an email + SMS with a link to create an account and sign.
                </p>
            </div>

            {/* ============ E-STAMP COST BREAKDOWN (if E-Stamp) ============ */}
            {agreementType === 'e_stamp' && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                borderRadius: '8px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ color: '#22c55e', marginTop: 0, marginBottom: '1rem' }}>💳 E-Stamp Agreement Cost Breakdown</h3>

                <div style={{ fontSize: '0.9rem', lineHeight: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(34, 197, 94, 0.2)', paddingBottom: '0.5rem' }}>
                    <span style={{ color: '#86efac' }}>Orello Platform Fee:</span>
                    <span style={{ color: '#22c55e', fontWeight: 600 }}>₹299</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(34, 197, 94, 0.2)', paddingBottom: '0.5rem' }}>
                    <span style={{ color: '#86efac' }}>Government Stamp Duty:</span>
                    <span style={{ color: '#22c55e', fontWeight: 600 }}>
                      ~₹{(() => {
                        const monthlyRent = parseInt(formData.monthly_rent) || 0;
                        const leaseMonths = (parseInt(formData.lease_duration_years) || 1) * 12;
                        const rate = 0.0025; // Default estimate
                        return (monthlyRent * leaseMonths * 12 * rate).toFixed(0);
                      })()}
                      <span style={{ fontSize: '0.8rem', color: '#86efac', marginLeft: '0.25rem' }}>(varies by state)</span>
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(34, 197, 94, 0.2)', paddingBottom: '0.5rem' }}>
                    <span style={{ color: '#86efac' }}>Sub-Registrar Registration Fee:</span>
                    <span style={{ color: '#22c55e', fontWeight: 600 }}>~₹1,100</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', fontSize: '1rem' }}>
                    <span style={{ color: '#f5f5f5', fontWeight: 600 }}>Estimated Total Cost:</span>
                    <span style={{ color: '#22c55e', fontWeight: 700 }}>
                      ~₹{(() => {
                        const monthlyRent = parseInt(formData.monthly_rent) || 0;
                        const leaseMonths = (parseInt(formData.lease_duration_years) || 1) * 12;
                        const stampDuty = (monthlyRent * leaseMonths * 12 * 0.0025);
                        return (299 + stampDuty + 1100).toFixed(0);
                      })()}
                    </span>
                  </div>
                </div>

                <p style={{ fontSize: '0.8rem', color: '#86efac', marginTop: '1rem', marginBottom: 0 }}>
                  <strong>Note:</strong> Stamp duty amount is estimated and varies by state. Exact cost will be calculated based on your state's stamp duty rate. Payment will be collected at checkout.
                </p>
              </div>
            )}

            {/* ============ COMPREHENSIVE DISCLAIMER & TERMS ============ */}
            <div style={{ marginTop: '2rem', borderTop: '2px solid rgba(249, 115, 22, 0.3)', paddingTop: '2rem' }}>

              {/* Expandable Disclaimer Section */}
              <div style={{
                borderRadius: '8px',
                background: 'rgba(249, 115, 22, 0.08)',
                border: '1px solid rgba(249, 115, 22, 0.3)',
                overflow: 'hidden',
                marginBottom: '1.5rem'
              }}>
                <button
                  onClick={() => setExpandedDisclaimer(!expandedDisclaimer)}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: 'rgba(249, 115, 22, 0.15)',
                    border: 'none',
                    color: '#F97316',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.3s'
                  }}
                >
                  <span>⚠️ E-Agreement Terms & Conditions (Tap to Read)</span>
                  <span style={{ transform: expandedDisclaimer ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>▼</span>
                </button>

                {expandedDisclaimer && (
                  <div style={{ padding: '1.5rem', background: 'rgba(249, 115, 22, 0.05)', fontSize: '0.85rem', color: '#f5f5f5', lineHeight: 1.8 }}>
                    <h4 style={{ color: '#F97316', marginTop: 0 }}>📋 E-Agreement Terms & Conditions - IMPORTANT NOTICE</h4>

                    <p><strong>What This Agreement IS:</strong></p>
                    <ul style={{ marginBottom: '1rem' }}>
                      <li>✓ Valid and binding between Lessor and Lessee for practical operations</li>
                      <li>✓ Digitally signed through Orello platform with legally valid eSignature (IT Act 2000)</li>
                      <li>✓ Legally binding under Indian Contract Act, 1872</li>
                      <li>✓ Can be used to enforce rent and deposit obligations between parties</li>
                    </ul>

                    <p><strong>What This Agreement IS NOT:</strong></p>
                    <ul style={{ marginBottom: '1rem' }}>
                      <li>✗ NOT government-stamped (no stamp duty affixed)</li>
                      <li>✗ NOT registered with Sub-Registrar office</li>
                      <li>✗ NOT court-valid in its current form</li>
                      <li>✗ NOT admissible as primary evidence in court proceedings</li>
                      <li>✗ NOT recognized by government/courts for legal enforcement</li>
                    </ul>

                    <p style={{ color: '#FCD34D', fontWeight: 600, marginBottom: '1rem', padding: '0.75rem', background: 'rgba(253, 224, 71, 0.1)', borderRadius: '4px' }}>
                      <strong>⚠️ Witness Information:</strong> Witnesses are NOT required for digital signing. The witness fields in this form are OPTIONAL. Fill them only if you plan to later print and stamp this agreement with Sub-Registrar office.
                    </p>

                    <p><strong>Witness Requirements - Key Facts:</strong></p>
                    <ul style={{ marginBottom: '1rem' }}>
                      <li>🔹 <strong>For digital signing:</strong> NO witnesses needed - digital signatures are legally valid under IT Act 2000</li>
                      <li>🔹 <strong>For printing & stamping:</strong> YES, 1-2 witnesses required (varies by state)</li>
                      <li>🔹 Witness must be: Adult (18+), not the Lessor/Lessee, should know both parties</li>
                      <li>🔹 Witnesses sign: ONLY when you print the agreement (not needed now)</li>
                      <li>🔹 Witness fields optional: You can fill now OR find witnesses later when stamping</li>
                    </ul>

                    <p><strong>Two Options - Choose What's Right for You:</strong></p>
                    <ul style={{ marginBottom: '1rem' }}>
                      <li>
                        <strong>✓ Option 1 - Use E-Agreement As-Is:</strong> Digital signature valid for practical operations (rent, occupancy). NO witnesses needed. NOT court-valid. Leave witness fields blank.
                      </li>
                      <li style={{ marginTop: '0.5rem' }}>
                        <strong>✓ Option 2 - Print, Stamp & Register:</strong> Print agreement, get 1-2 witnesses to sign printed copy, stamp with government, register with Sub-Registrar. Becomes court-valid. Fill witness fields for future reference.
                      </li>
                    </ul>

                    <p><strong>Stamping & Registration Responsibility (If Choosing Option 2):</strong></p>
                    <ul style={{ marginBottom: '1rem' }}>
                      <li>🔹 Orello creates template ONLY - does NOT handle stamping or registration</li>
                      <li>🔹 Print agreement: Both parties responsible (get witnesses to sign printed copy)</li>
                      <li>🔹 Stamp duty: Paid by Lessor (₹500-10,000+ depending on property/state)</li>
                      <li>🔹 Registration fee: ₹500-1,500 (both parties' responsibility)</li>
                      <li>🔹 Timeline: Complete all steps within 7 days to avoid penalties</li>
                    </ul>

                    <p><strong>Orello's Liability & Limitations:</strong></p>
                    <ul style={{ marginBottom: '1rem' }}>
                      <li>❌ Orello is NOT responsible for stamping and registration</li>
                      <li>❌ Orello is NOT responsible for witness verification or authentication</li>
                      <li>❌ Orello is NOT liable for consequences of non-stamping/registration</li>
                      <li>❌ Orello is NOT a law firm and does not provide legal advice</li>
                      <li>❌ Orello does NOT guarantee court validity without official stamping</li>
                    </ul>

                    <p><strong>Your Responsibilities:</strong></p>
                    <ul>
                      <li>✓ Sign digitally through Orello (witnesses not needed for this step)</li>
                      <li>✓ Decide: Use Option 1 (as-is) or Option 2 (print & stamp)</li>
                      <li>✓ If Option 2: Download and print agreement</li>
                      <li>✓ If Option 2: Find 1-2 adult witnesses (any adult 18+ can be witness)</li>
                      <li>✓ If Option 2: Get witnesses to sign printed copy of agreement</li>
                      <li>✓ If Option 2: Get it stamped with government e-stamp</li>
                      <li>✓ If Option 2: Register with Sub-Registrar office (bring witness details)</li>
                      <li>✓ Keep stamped and registered copies safely for entire lease period</li>
                      <li>✓ Verify state-specific stamp duty rates and witness requirements</li>
                    </ul>

                    <p style={{ color: '#FCA5A5', fontWeight: 600 }}>
                      ⚠️ CRITICAL: Digital signature is valid and binding. But agreement is NOT court-valid without stamping (Option 2). Choose the option that fits your needs.
                    </p>
                  </div>
                )}
              </div>

              {/* Checkbox Agreement */}
              <div style={{
                padding: '1rem',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '6px',
                marginBottom: '1.5rem'
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    style={{ marginTop: '0.3rem', cursor: 'pointer', accentColor: '#F97316' }}
                  />
                  <span style={{ fontSize: '0.9rem', color: '#86efac', lineHeight: 1.6 }}>
                    <strong>I have read and agree to the E-Agreement Terms & Conditions above.</strong>
                  </span>
                </label>
              </div>
            </div>
          </div>
        );

      case 9: {
        // Brokerage — runs AFTER the agreement exists. Optional: a broker may
        // charge nothing and simply skip.
        const t = Number(brokerageFromTenant || 0);
        const l = Number(brokerageFromLandlord || 0);
        const total = (t + l) || 0;
        return (
          <div className="step-content">
            <h2>Step 9: Your Brokerage</h2>
            <p style={{ color: '#a3a3a3', marginBottom: '1.5rem' }}>
              Charge your brokerage through Orello. We collect it from the parties and pass on
              <strong style={{ color: '#10b981' }}> 100% — Orello takes no cut</strong>. It lands in your
              Orello wallet and you can withdraw it immediately.
            </p>

            <label>Brokerage from tenant (₹):</label>
            <input type="number" min="0" placeholder="0" value={brokerageFromTenant}
              onChange={(e) => setBrokerageFromTenant(e.target.value)} />

            <label style={{ marginTop: '1rem' }}>Brokerage from landlord (₹):</label>
            <input type="number" min="0" placeholder="0" value={brokerageFromLandlord}
              onChange={(e) => setBrokerageFromLandlord(e.target.value)} />

            {total > 0 && (
              <div style={{
                marginTop: '1.25rem', padding: '1rem', borderRadius: '8px',
                background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#f5f5f5', fontWeight: 700 }}>
                  <span>You'll receive</span>
                  <span style={{ color: '#10b981' }}>₹{total.toLocaleString('en-IN')}</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a3a3a3', marginTop: '0.35rem' }}>
                  Orello platform fee on brokerage: ₹0
                </div>
              </div>
            )}

            <div style={{
              marginTop: '1.25rem', padding: '0.85rem 1rem', borderRadius: '8px',
              background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)',
              fontSize: '0.8rem', color: '#fbbf24',
            }}>
              ⚠️ The tenancy won't go active until the brokerage you set here is paid. Leave both blank
              if you're not charging any — you can also set or change this later from the rental page.
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        .form-with-preview {
          padding: 24px;
          max-width: 1600px;
          margin: 0 auto;
        }

        /* Steps 1-7: Full width form */
        .form-with-preview.form-layout {
          display: block;
        }

        /* Step 8: Split layout with preview */
        .form-with-preview.step-8-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
        }

        @media (max-width: 1200px) {
          .form-with-preview.step-8-layout {
            grid-template-columns: 1fr;
          }
          .preview-section {
            order: 2;
          }
          .form-section {
            order: 1;
          }
        }

        .form-section {
          background: #1a1a1a;
          padding: 28px;
          border-radius: 12px;
          border: 1px solid #333;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }

        .preview-section {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          border: 1px solid #e5e5e5;
        }

        .progress-bar {
          display: flex;
          gap: 8px;
          margin-bottom: 28px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .progress-step {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #333;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
          color: #888;
        }

        .progress-step.active {
          background: #F97316;
          color: #fff;
          box-shadow: 0 0 15px rgba(249,115,22,0.5);
        }

        .error-messages {
          padding: 12px;
          background: rgba(206, 40, 40, 0.1);
          border: 1px solid rgba(206, 40, 40, 0.3);
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .error-messages p {
          color: #ff6b6b;
          margin: 4px 0;
          font-size: 14px;
        }

        .step-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .step-content h2 {
          color: #F97316;
          font-size: 20px;
          margin-bottom: 12px;
          font-weight: bold;
        }

        .step-content input,
        .step-content textarea,
        .step-content select {
          padding: 12px;
          background: #2a2a2a;
          border: 1px solid #444;
          border-radius: 8px;
          color: #f5f5f5;
          font-size: 14px;
          transition: all 0.3s;
        }

        .step-content input:focus,
        .step-content textarea:focus,
        .step-content select:focus {
          outline: none;
          border-color: #F97316;
          box-shadow: 0 0 8px rgba(249,115,22,0.3);
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 28px;
        }

        .form-actions button {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 6px;
          font-size: 15px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s;
        }

        .form-actions button:first-child {
          background: #333;
          color: #f5f5f5;
          border: 1px solid #555;
        }

        .form-actions button:first-child:hover:not(:disabled) {
          background: #444;
        }

        .btn-primary {
          background: #F97316 !important;
          color: white !important;
        }

        .btn-primary:hover:not(:disabled) {
          background: #EA580C !important;
        }

        .form-actions button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      {/* Step 9 (brokerage) is a plain form — only step 8 uses the split preview layout. */}
      <div className={`form-with-preview ${currentStep === 8 ? 'step-8-layout' : 'form-layout'}`}>
      <div className="form-section">
        <div className="progress-bar">
          {/* State Selection Step */}
          <div
            className={`progress-step ${currentStep >= -1 ? 'active' : ''}`}
            onClick={() => currentStep >= -1 && setCurrentStep(-1)}
            title="State Selection"
            style={{ fontSize: '0.8rem', fontWeight: 600 }}
          >
            S
          </div>
          {/* Step 0 */}
          <div
            className={`progress-step ${currentStep >= 0 ? 'active' : ''}`}
            onClick={() => currentStep >= 0 && setCurrentStep(0)}
          >
            0
          </div>
          {/* Steps 1-8 (plus step 9 for brokers/agents) */}
          {Array.from({ length: lastStep }, (_, i) => (
            <div
              key={i + 1}
              className={`progress-step ${i + 1 <= (currentStep > 0.5 ? currentStep : 0) ? 'active' : ''}`}
              onClick={() => {
                // Backward nav is hard-locked at step 9: the agreement already
                // exists by then, and returning to step 8 would let Complete
                // fire a second time and create a duplicate rental + agreement.
                if (currentStep === 9) return;
                if ((currentStep > 0.5 ? currentStep : 0) >= i + 1) setCurrentStep(i + 1);
              }}
            >
              {i + 1}
            </div>
          ))}
        </div>

        {errors.length > 0 && (
          <div className="error-messages">
            {errors.map((error, idx) => <p key={idx}>❌ {error}</p>)}
          </div>
        )}

        {currentStep < 8 ? (
          // Steps 1-7: Full width form
          <div style={{ maxWidth: '100%' }}>
            {renderStep()}
          </div>
        ) : (
          // Step 8: Show full agreement preview on right side
          <div style={{ maxWidth: '100%' }}>
            {renderStep()}
          </div>
        )}

        <div className="form-actions" style={{ gap: '8px' }}>
          <button
            onClick={() => {
              // Never navigate back out of step 9 — see the progress-bar note.
              if (currentStep === 9) return;
              if (currentStep === 0.5) setCurrentStep(0);
              else if (currentStep === 0) setCurrentStep(-1);
              else if (currentStep === 8 && isCreator) {
                // If creator is on Step 8 (skipped Step 7), go back to Step 6
                setCurrentStep(6);
              }
              else if (currentStep > 0) setCurrentStep(currentStep - 1);
            }}
            disabled={currentStep === -1 || currentStep === 9}
          >
            ← Back
          </button>
          {/* Draft-saving is meaningless at step 9 — the agreement already exists. */}
          {currentStep !== 9 && (
            <button
              onClick={handleSaveClick}
              disabled={loading}
              style={{
                backgroundColor: '#666',
                color: '#fff',
                flex: 0.8
              }}
              title="Save your progress. You can continue editing later."
            >
              {loading ? '⏳ Saving...' : '💾 Save Draft'}
            </button>
          )}
          <button
            onClick={submitStep}
            disabled={loading || (currentStep === 8 && !agreedToTerms) || (currentStep >= 1 && currentStep <= 7 && validateAllFieldsInStep().length > 0)}
            className="btn-primary"
            title={
              currentStep === 8 && !agreedToTerms
                ? 'Please agree to terms and conditions first'
                : (currentStep >= 1 && currentStep <= 7 && validateAllFieldsInStep().length > 0)
                ? `Please fill all required fields: ${validateAllFieldsInStep().join(', ')}`
                : ''
            }
          >
            {loading
              ? '⏳ Saving...'
              : currentStep === 9
              ? ((Number(brokerageFromTenant || 0) + Number(brokerageFromLandlord || 0)) > 0
                  ? '✅ Save brokerage & finish'
                  : 'Skip — no brokerage')
              : currentStep === 8
              ? (agreedToTerms ? '✅ Complete' : '⚠️ Agree to Terms')
              : 'Next →'}
          </button>
        </div>
      </div>
      </div>

      {/* Save As Dialog Modal */}
      {showSaveAsDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            border: '1px solid #404040',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <h2 style={{
              color: '#F97316',
              marginBottom: '1rem',
              fontSize: '1.5rem',
              fontWeight: 600
            }}>
              💾 Save Agreement As
            </h2>

            <p style={{
              color: '#a3a3a3',
              marginBottom: '1.5rem',
              fontSize: '0.95rem'
            }}>
              Enter a name for this agreement. You can reference it later from your dashboard.
            </p>

            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              color: '#f5f5f5',
              fontWeight: 600,
              fontSize: '0.9rem'
            }}>
              Agreement Name
            </label>

            <input
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !loading) saveAgreementDraft();
              }}
              placeholder="e.g., Mumbai Flat - Landlord & Tenant"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '8px',
                color: '#f5f5f5',
                fontSize: '0.95rem',
                marginBottom: '1.5rem',
                boxSizing: 'border-box',
                outline: 'none'
              }}
              autoFocus
            />

            <div style={{
              fontSize: '0.8rem',
              color: '#888',
              marginBottom: '1.5rem',
              padding: '0.75rem',
              background: 'rgba(249,115,22,0.1)',
              borderRadius: '6px',
              border: '1px solid rgba(249,115,22,0.2)'
            }}>
              💡 <strong>Tip:</strong> Use a descriptive name with property location and parties involved for easy identification.
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowSaveAsDialog(false);
                  setDraftName('');
                }}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#444',
                  color: '#f5f5f5',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>

              <button
                onClick={saveAgreementDraft}
                disabled={loading || !draftName.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: draftName.trim() ? '#F97316' : '#666',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: draftName.trim() && !loading ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  transition: 'all 0.2s',
                  opacity: (draftName.trim() && !loading) ? 1 : 0.6
                }}
              >
                {loading ? '⏳ Saving...' : '💾 Save Agreement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgreementFormWithPreview;
